import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { diffArrays } from 'diff';
import Redline from '../components/Redline';
import Modal from '../components/Modal';
import CommentThread from '../components/CommentThread';
import VersionRail from '../components/VersionRail';
import { useToast } from '../components/Toast';
import { useAuth } from '../AuthContext';
import { useOrg } from '../data/orgs';
import { useEntityDoc, useEntityList, updateEntity, createEntity } from '../data/entities';
import { useDealPowers } from '../data/permissions';
import { publishVersion, loadVersionClauses, actorOf, PARTY_COLORS, MANIFEST_SOURCE_LABEL, FACT_TYPE_LABEL, cabinetOf, decisionOf, DP_DECISION_LABELS, docViewOf, docTypeLabel } from '../data/deals';
import DocTypePicker from '../components/DocTypePicker';
import { useDocTypeCatalog } from '../data/docTypes';
import { aiSummarize, aiRisks, aiCompare, aiDraft, aiDocAgent, aiGenerateMemo, verifyChain, downloadWordExport, deleteDocument, isAiConfigured } from '../data/legalApi';
import { textBlocks, parseSlideBody, applyOpsWithManifest, invertManifestOp, canRevert } from '../lib/docBlocks';
import { computeFactConflicts, conflictQuestion } from '../data/factReview';
import { uploadFile, isUploadConfigured, downloadAsset, getAssetDownloadUrl, safeFileSeg } from '../data/uploadService';
import TaskProgress from '../components/TaskProgress';
import { useDoc } from '../data/firestore';
import { HelpDot, Term } from '../components/GuidePanel';
import SpotlightTour from '../components/SpotlightTour';
import { CommandPanel } from '../components/CommandBar';
import WorkbookView from '../components/WorkbookView';
import WordPages from '../components/WordLayout';
// (Term renders a glossary chip — used in prose spots below, never inside
// another button since it is itself a <button>.)

// The full live feed for a running server task: subscribes to the org task
// registry entry (steps + backend log stream) pointed at by the slim
// `progress` field on the document.
function LiveTaskFeed({ scope, progress }) {
  const { doc: task } = useDoc(progress?.taskId ? `orgs/${scope.org}/tasks/${progress.taskId}` : null);
  return <TaskProgress progress={progress} task={task || null} />;
}

// Build stamp — confirms the browser is running the latest bundle.
console.log('[fast.legal] DocView bundle 2026-08-25 18:00 PT (new-draft click logging)');

// Word-level redline lives in components/Redline.js (shared with the
// project audit trail's expandable change detail).

// Side-by-side row alignment: split both versions of a clause into
// paragraphs and pair them up with a sequence diff, so the two columns stay
// horizontally synced — when one side grows, the other side gets whitespace
// and the next paragraph starts level again. Each row is { left, right }
// (null = that side has nothing there).
function alignSxsRows(baseText, curText) {
  if (!baseText) return [{ left: null, right: curText }];
  // Tables don't split cleanly across paragraph rows — keep them whole.
  if (/^\s*\|.*\|/m.test(baseText) || /^\s*\|.*\|/m.test(curText)) {
    return [{ left: baseText, right: curText }];
  }
  const paras = (t) => t.split(/\n\s*\n/);
  const rows = [];
  let pendingLeft = [];
  for (const part of diffArrays(paras(baseText), paras(curText))) {
    if (part.removed) {
      pendingLeft.push(...part.value);
      continue;
    }
    if (part.added) {
      // A removed run followed by an added run is a rewrite — pair them up.
      const n = Math.max(pendingLeft.length, part.value.length);
      for (let i = 0; i < n; i++) {
        rows.push({ left: pendingLeft[i] ?? null, right: part.value[i] ?? null });
      }
      pendingLeft = [];
      continue;
    }
    for (const l of pendingLeft) rows.push({ left: l, right: null });
    pendingLeft = [];
    for (const v of part.value) rows.push({ left: v, right: v });
  }
  for (const l of pendingLeft) rows.push({ left: l, right: null });
  return rows;
}

// Clause body renderer — markdown pipe tables become real tables, "- item"
// lines become bullets, "**line**" becomes a bold sub-heading (matching the
// Word/PDF exports), everything else stays pre-wrapped text.
const CB_BULLET = /^-\s+(.*)$/;
const CB_BOLD = /^\*\*(.+)\*\*$/;

function ProseBlock({ text }) {
  const parts = [];
  let buf = [];
  let items = [];
  const flushBuf = () => {
    const t = buf.join('\n');
    buf = [];
    if (t.trim()) parts.push({ t: 'p', text: t });
  };
  const flushItems = () => {
    if (items.length) parts.push({ t: 'ul', items });
    items = [];
  };
  for (const line of text.split('\n')) {
    const b = line.match(CB_BULLET);
    const h = line.match(CB_BOLD);
    if (b) { flushBuf(); items.push(b[1]); }
    else if (h) { flushBuf(); flushItems(); parts.push({ t: 'h', text: h[1] }); }
    else { flushItems(); buf.push(line); }
  }
  flushBuf();
  flushItems();
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
      {parts.map((p, i) =>
        p.t === 'h' ? (
          <strong key={i}>{p.text}</strong>
        ) : p.t === 'ul' ? (
          <ul key={i} style={{ margin: 0, paddingLeft: 22 }}>
            {p.items.map((it, j) => <li key={j}>{it}</li>)}
          </ul>
        ) : (
          <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{p.text}</span>
        )
      )}
    </div>
  );
}

function ClauseText({ text }) {
  const blocks = useMemo(() => textBlocks(text), [text]);
  const rich = blocks.some((b) => b.type === 'table') || /^(-\s|\*\*.+\*\*$)/m.test(text || '');
  if (!rich) {
    return <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>;
  }
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      {blocks.map((b, i) =>
        b.type === 'table' ? (
          <div key={i} className="u-scroll-x">
            <table className="cs-table cs-doc-table">
              {b.header && (
                <thead><tr>{b.rows[0].map((c, j) => <th key={j}>{c}</th>)}</tr></thead>
              )}
              <tbody>
                {(b.header ? b.rows.slice(1) : b.rows).map((r, ri) => (
                  <tr key={ri}>{r.map((c, ci) => <td key={ci}>{c}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <ProseBlock key={i} text={b.text} />
        )
      )}
    </div>
  );
}

// Slide body renderer for decks — bullets, lead-in paragraphs, tables, and
// speaker notes, matching what the PowerPoint export produces.
function SlideBody({ text }) {
  const { bullets, paras, tables, notes } = useMemo(() => parseSlideBody(text), [text]);
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
      {paras.map((p, i) => <p key={`p${i}`} className="u-small" style={{ margin: 0 }}>{p}</p>)}
      {bullets.length > 0 && (
        <ul className="u-small" style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7 }}>
          {bullets.map((b, i) => (
            <li key={i} style={{ marginLeft: b.indent * 16, listStyleType: b.indent ? 'circle' : 'disc' }}>{b.text}</li>
          ))}
        </ul>
      )}
      {tables.map((t, i) => (
        <div key={`t${i}`} className="u-scroll-x">
          <table className="cs-table cs-doc-table">
            {t.header && <thead><tr>{t.rows[0].map((c, j) => <th key={j}>{c}</th>)}</tr></thead>}
            <tbody>
              {(t.header ? t.rows.slice(1) : t.rows).map((r, ri) => (
                <tr key={ri}>{r.map((c, ci) => <td key={ci}>{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {notes && (
        <p className="u-xs u-muted" style={{ margin: 0, fontStyle: 'italic', borderTop: '1px dashed var(--border)', paddingTop: 'var(--sp-2)' }}>
          Speaker notes: {notes}
        </p>
      )}
    </div>
  );
}

// Changes manifest viewer — the traceability record of one version: which
// ops produced it (full before/after), what caused them (instruction /
// proposal / manual edit), and a per-op targeted revert that publishes a
// corrective version against the CURRENT draft.
function ChangesModal({ scope, doc, version, official, dealPoints, participant, isDeck, onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(-1);
  const m = version.manifest;
  const unit = isDeck ? 'slide' : 'section';

  const revert = async (mOp, idx) => {
    setBusy(idx);
    try {
      const inverted = invertManifestOp(mOp);
      const { clauses, manifestOps } = applyOpsWithManifest(official.clauses, [inverted]);
      if (!manifestOps.length) throw new Error(`that ${unit} has changed since — revert it manually`);
      const label = `Revert: ${mOp.op} "${(mOp.before || mOp.after)?.heading || mOp.id}" (from v${version.number})`;
      await publishVersion(scope, actorOf(user, participant), doc, {
        clauses,
        label: label.slice(0, 120),
        note: `Targeted revert of one change from v${version.number}'s manifest`,
        baseVersionId: official.id,
        dealPoints,
        manifest: { source: 'revert', instruction: label, revertOfVersionId: version.id, ops: manifestOps },
      });
      toast('Reverted — published as a new version', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(-1);
    }
  };

  return (
    <Modal title={`Changes manifest — v${version.number}${version.label ? ` (${version.label})` : ''}`} onClose={onClose} maxWidth={880}>
      {!m ? (
        <p className="u-small u-muted">
          No manifest on this version — it predates change tracking or was imported. The redline view against its base still shows every difference.
        </p>
      ) : (
        <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
          <div className="u-small u-muted">
            Source: <strong>{MANIFEST_SOURCE_LABEL[m.source] || m.source}</strong>
            {m.mode === 'vibe' && <span className="cs-chip" style={{ marginLeft: 'var(--sp-2)' }}>vibe — applied without review</span>}
            {m.via ? ` via ${m.via}` : ''} · by {version.authorName} · {new Date(version.createdAt).toLocaleString()}
            {m.appliedOps !== undefined && ` · ${m.appliedOps} of ${m.proposedOps} proposed changes applied`}
            {m.sourceDocId && (
              <>
                {' · '}
                <Link to={`/deals/${scope.deal}/docs/${m.sourceDocId}`}>source document (v{m.sourceVersionNumber})</Link>
              </>
            )}
            {m.instruction && (
              <div style={{ marginTop: 'var(--sp-1)' }}>Instruction: “{m.instruction}”</div>
            )}
          </div>
          <div className="u-stack" style={{ '--gap': 'var(--sp-3)', maxHeight: 480, overflow: 'auto' }}>
            {(m.ops || []).map((op, i) => {
              const revertable = official && canRevert(op, official.clauses);
              return (
                <div key={i} className="cs-card u-stack" style={{ '--gap': 'var(--sp-2)' }}>
                  <div className="u-split">
                    <span className="u-small">
                      <span className={`cs-badge ${op.op === 'add' ? 'b-ok' : op.op === 'remove' ? 'b-danger' : 'b-info'}`}>{op.op}</span>{' '}
                      <strong>{(op.after || op.before)?.heading || op.id}</strong>
                    </span>
                    <button
                      className="cs-btn cs-btn-outline cs-btn-sm"
                      disabled={busy !== -1 || !revertable}
                      data-tip={revertable ? 'Undo just this change — publishes a corrective version' : `The ${unit} changed since — revert manually`}
                      onClick={() => revert(op, i)}
                    >
                      {busy === i ? 'Reverting…' : 'Revert this change'}
                    </button>
                  </div>
                  {op.op === 'edit' && (
                    <div className="u-small" style={{ maxHeight: 180, overflow: 'auto' }}>
                      <Redline before={op.before?.text || ''} after={op.after?.text || ''} />
                    </div>
                  )}
                  {op.op === 'add' && (
                    <div className="u-small" style={{ whiteSpace: 'pre-wrap', maxHeight: 140, overflow: 'auto', color: 'var(--ok)' }}>
                      {op.after?.text}
                    </div>
                  )}
                  {op.op === 'remove' && (
                    <div className="u-small" style={{ whiteSpace: 'pre-wrap', maxHeight: 140, overflow: 'auto' }}>
                      <del style={{ color: 'var(--danger)', opacity: 0.8 }}>{op.before?.text}</del>
                    </div>
                  )}
                </div>
              );
            })}
            {!(m.ops || []).length && <span className="u-small u-muted">Manifest has no ops recorded.</span>}
          </div>
          <p className="u-xs u-muted" style={{ margin: 0 }}>
            Reverts are surgical: only the selected change is undone, applied against the current draft as a new
            immutable version (itself manifested, sealed, and auditable).
          </p>
        </div>
      )}
    </Modal>
  );
}

// Received a new turn of this agreement? Upload it here: the ingest worker
// aligns its clauses to the existing ids (deal point anchors and the graph
// survive), publishes it as the next version with a full manifest, runs an
// AI change brief, and flags every deal point whose language moved.
//
// A checkpoint often arrives as the same draft in several formats (.docx +
// its PDF print). All selected files are uploaded and kept on the version;
// the best structured format is ranked the SOURCE and is the one ingested.
const SOURCE_RANK = { docx: 0, doc: 1, rtf: 2, md: 3, txt: 4, eml: 5, pdf: 6 };
const fileExt = (name) => (name.match(/\.([a-z0-9]+)$/i)?.[1] || '').toLowerCase();
function rankDraftFiles(files) {
  const ranked = [...files].sort(
    (a, b) => (SOURCE_RANK[fileExt(a.name)] ?? 9) - (SOURCE_RANK[fileExt(b.name)] ?? 9)
  );
  return ranked.map((f, i) => ({ file: f, role: i === 0 ? 'source' : 'reference' }));
}

function NewDraftModal({ scope, deal, doc, onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const fileRef = useRef(null);
  const [picked, setPicked] = useState([]);
  const [label, setLabel] = useState('');
  const [partyKey, setPartyKey] = useState('');
  const [context, setContext] = useState('');
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const parties = deal?.parties || [];

  useEffect(() => {
    console.log('[fast.legal] NewDraftModal opened', { docId: doc.id, dealS3Prefix: deal?.s3Prefix, uploadConfigured: isUploadConfigured() });
  }, [doc.id, deal?.s3Prefix]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const ranked = rankDraftFiles(Array.from(fileRef.current?.files || []));
      if (!ranked.length) throw new Error('Choose the received file(s) first');
      if (!isUploadConfigured()) throw new Error('Upload backend not configured');
      const stamp = Date.now();
      const rawPrefix = deal?.s3Prefix;
      const s3Prefix = (rawPrefix && String(rawPrefix).startsWith('items/'))
        ? (rawPrefix.endsWith('/') ? rawPrefix : `${rawPrefix}/`)
        : `items/${deal.id}/`;
      const totalBytes = ranked.reduce((s, r) => s + r.file.size, 0) || 1;
      let doneBytes = 0;
      setProgress(0);
      const uploaded = [];
      for (const { file, role } of ranked) {
        const safeName = safeFileSeg(file.name);
        const key = `${s3Prefix}source/${stamp}-${safeName}`;
        await uploadFile(file, {
          key,
          onProgress: (f) => setProgress((doneBytes + f * file.size) / totalBytes),
        });
        doneBytes += file.size;
        setProgress(doneBytes / totalBytes);
        uploaded.push({ name: file.name, s3Key: key, size: file.size, contentType: file.type || '', role });
      }
      const source = uploaded.find((f) => f.role === 'source');
      await updateEntity('dealDoc', scope, doc.id, {
        newDraft: {
          s3Key: source.s3Key,
          files: uploaded,
          label: label.trim() || `Received: ${source.name.replace(/\.[^.]+$/, '')}`,
          context: context.trim(),
          partyKey,
          uploadedBy: user.uid,
          uploadedByName: user.displayName || user.email || '',
        },
        status: 'queued',
      });
      toast('New draft queued — ingesting as the next version', 'ok');
      onClose();
    } catch (err) {
      console.error('[fast.legal] new-draft upload FAILED:', err);
      toast(err.message, 'danger');
      setBusy(false);
      setProgress(null);
    }
  };

  const ranked = rankDraftFiles(picked);

  return (
    <Modal title={`Upload new draft — ${doc.title}`} onClose={onClose}>
      <form className="u-stack" style={{ '--gap': 'var(--sp-4)' }} onSubmit={submit}>
        <p className="u-small u-muted" style={{ margin: 0 }}>
          The received draft becomes <strong>v{(doc.versionCount || 1) + 1}</strong> of this document. Sections are matched
          to the current draft so the redline, deal point links, and knowledge graph stay intact, and AI briefs
          you on what changed.
        </p>
        <div className="cs-field">
          <label htmlFor="nd-file">Received files (.docx, .pdf, .txt)</label>
          <input
            id="nd-file"
            ref={fileRef}
            type="file"
            className="cs-input"
            accept=".docx,.doc,.pdf,.txt,.rtf,.md,.eml,.xlsx,.xlsm"
            multiple
            onChange={(e) => setPicked(Array.from(e.target.files || []))}
          />
          <div className="cs-help">
            Upload everything that arrived for this checkpoint — a turn often comes as a .docx plus its PDF
            print. The Word file is ingested as the draft (tracked changes accepted automatically); the rest
            are preserved on the version as hashed reference copies.
          </div>
          {ranked.length > 0 && (
            <div className="u-stack" style={{ '--gap': '4px', marginTop: 'var(--sp-2)' }}>
              {ranked.map(({ file, role }) => (
                <span key={file.name} className="u-small u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
                  <span className={`cs-badge ${role === 'source' ? 'b-ok' : ''}`}>
                    {role === 'source' ? 'draft source' : 'reference'}
                  </span>
                  <span>{file.name}</span>
                  <span className="u-xs u-muted u-tabular">{(file.size / 1024).toFixed(0)} KB</span>
                </span>
              ))}
            </div>
          )}
        </div>
        {parties.length > 0 && (
          <div className="cs-field">
            <label htmlFor="nd-party">From which side?</label>
            <select id="nd-party" className="cs-input" value={partyKey} onChange={(e) => setPartyKey(e.target.value)}>
              <option value="">Unspecified</option>
              {parties.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
        )}
        <div className="cs-field">
          <label htmlFor="nd-label">Version label</label>
          <input id="nd-label" className="cs-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Seller counter — round 3" maxLength={200} />
        </div>
        <div className="cs-field">
          <label htmlFor="nd-context">Context & correspondence (optional)</label>
          <textarea
            id="nd-context"
            className="cs-input"
            rows={7}
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={'Paste anything that arrived with the draft — your lawyer\'s feedback, the counterparty\'s cover email or summary, the whole email chain…'}
          />
          <div className="cs-help">
            Preserved verbatim on the version record. The AI review uses it to attribute intent, surface each
            side's key asks, and cross-check whether the draft actually delivers what the correspondence claims.
          </div>
        </div>
        {progress !== null && (
          <div role="status" className="u-small u-muted u-tabular">Uploading… {Math.round(progress * 100)}%</div>
        )}
        <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy}>
            {busy ? 'Uploading…' : 'Ingest as new version'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Document → deck conversion: the agent distills the current version into a
// new presentation; the deck's v1 manifest records the source doc + version.
function ConvertToDeckModal({ scope, doc, onClose }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [guidance, setGuidance] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await aiDocAgent({ orgId: scope.org, dealId: scope.deal, convertDocId: doc.id, instruction: guidance.trim() });
      toast(`"${r.title}" created — ${r.sections} slides from v${r.sourceVersionNumber}`, 'ok');
      navigate(`/deals/${scope.deal}/docs/${r.docId}`);
    } catch (err) {
      toast(err.message, 'danger');
      setBusy(false);
    }
  };

  return (
    <Modal title={`Convert to presentation — ${doc.title}`} onClose={onClose} maxWidth={620}>
      <form className="u-stack" style={{ '--gap': 'var(--sp-4)' }} onSubmit={submit}>
        <p className="u-small u-muted" style={{ margin: 0 }}>
          The agent distills the current draft into a slide deck — narrative arc, key decisions, numbers, and tables;
          boilerplate dropped. The deck is a new document with its own versions; the original is untouched.
        </p>
        <div className="cs-field">
          <label htmlFor="conv-guide">Guidance (optional)</label>
          <textarea
            id="conv-guide"
            className="cs-input"
            rows={4}
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
            placeholder='e.g. "For an executive audience, 10 slides max, lead with the risk findings, keep the pricing table"'
          />
        </div>
        <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy}>
            {busy ? 'Converting (may take a minute)…' : 'Convert to deck'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Document outline (headings by level) — the in-app table of contents for
// structured documents. Clicking scrolls to the section.
function OutlinePanel({ clauses, targetId }) {
  return (
    <nav className="cs-card u-stack" style={{ '--gap': '2px', maxHeight: 360, overflow: 'auto' }} aria-label="Document outline">
      <strong className="u-xs u-muted" style={{ marginBottom: 'var(--sp-2)' }}>OUTLINE</strong>
      {clauses.map((c) => (
        <Link
          key={c.id}
          to={`?clause=${c.id}`}
          className="u-small"
          style={{
            textDecoration: 'none',
            color: c.id === targetId ? 'var(--accent)' : 'var(--text)',
            paddingLeft: ((c.level || 1) - 1) * 16,
            fontWeight: (c.level || 1) === 1 ? 600 : 400,
            lineHeight: 1.7,
          }}
        >
          {c.heading}
        </Link>
      ))}
    </nav>
  );
}

// ---- Key facts panel -----------------------------------------------------------
// The "don't miss a detail" layer: entities, jurisdictions, dates, amounts,
// percentages, and addresses parsed deterministically out of the document
// (shared/docGraph.js extractFacts, riding on the knowledge graph). When a
// document is repurposed — old agreement, new entity/purpose — every one of
// these is a candidate for change; each fact deep-links to every section
// it appears in and offers a tracked replace-everywhere.
//
// The NEEDS-REVIEW queue up top turns each flagged conflict into an explicit
// question with real answer paths: accept the suggested replacement, rule
// that both values are intentional (recorded on the doc so the warning stops
// resurfacing), or give a different answer — exact text, or natural language
// handed to the drafting agent.
function ConflictCard({ scope, doc, conflict, headingById, canReplace, onReplace, participant }) {
  const { user } = useAuth();
  const toast = useToast();
  const [answering, setAnswering] = useState(false); // "different answer" open
  const [keeping, setKeeping] = useState(false); // "both correct" note open
  const [text, setText] = useState('');
  const [busy, setBusy] = useState('');
  const c = conflict;

  const record = async (decision, note) => {
    await updateEntity('dealDoc', scope, doc.id, {
      factReviews: {
        [c.key]: {
          decision,
          note: String(note || '').slice(0, 500),
          value: c.fact.value,
          suggested: c.suggested.value,
          byName: user?.displayName || user?.email || 'user',
          at: Date.now(),
        },
      },
    });
  };

  const keepBoth = async () => {
    setBusy('keep');
    try {
      await record('keep', text);
      toast('Recorded — both values stand and this stops being flagged.', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy('');
      setKeeping(false);
      setText('');
    }
  };

  const askAgent = async () => {
    setBusy('agent');
    try {
      const r = await aiDocAgent({
        orgId: scope.org, dealId: scope.deal, docId: doc.id, mode: 'vibe',
        instruction:
          `Key-facts review of this draft flagged the ${FACT_TYPE_LABEL[c.type] || c.type} ` +
          `"${c.fact.value}" (used ${c.fact.count}×) conflicting with "${c.suggested.value}" (used ${c.suggested.count}×). ` +
          `The reviewer's ruling: ${text.trim()}\n` +
          'Apply exactly what the ruling requires and nothing else.',
      });
      await record('replaced', `via AI: ${text.trim()}`);
      toast(`Done — published v${r.number}: ${r.summary || 'applied'}.`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy('');
      setAnswering(false);
      setText('');
    }
  };

  if (c.resolution) {
    return (
      <div className="u-small u-split" style={{ gap: 'var(--sp-2)', opacity: 0.75 }}>
        <span style={{ minWidth: 0 }}>
          ✅ <strong>{c.fact.value}</strong>{' '}
          <span className="u-muted">
            — {c.resolution.decision === 'keep' ? 'ruled intentional' : 'replaced'}
            {c.resolution.note ? ` (${c.resolution.note})` : ''}
          </span>
        </span>
        <button
          type="button" className="cs-btn cs-btn-ghost cs-btn-sm" style={{ flexShrink: 0 }} disabled={busy === 'undo'}
          onClick={async () => {
            setBusy('undo');
            try { await updateEntity('dealDoc', scope, doc.id, { factReviews: { [c.key]: null } }); } catch (err) { toast(err.message, 'danger'); }
            setBusy('');
          }}
        >
          Undo
        </button>
      </div>
    );
  }

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-2)', borderLeft: '3px solid var(--warn)', paddingLeft: 'var(--sp-3)' }}>
      <span className="u-small">⚠️ {conflictQuestion(c)}</span>
      <span className="u-cluster" style={{ '--gap': 'var(--sp-1)' }}>
        <span className="u-xs u-muted">“{c.fact.value}” appears in:</span>
        {(c.fact.clauseIds || []).slice(0, 4).map((id) => (
          <Link key={id} to={`?clause=${id}`} className="cs-chip u-xs" title={headingById[id] || id}>
            {(headingById[id] || id).slice(0, 26)}
          </Link>
        ))}
        {(c.fact.clauseIds || []).length > 4 && <span className="u-xs u-muted">+{c.fact.clauseIds.length - 4} more</span>}
      </span>
      {!answering && !keeping && (
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
          {canReplace && (
            <button
              type="button" className="cs-btn cs-btn-primary cs-btn-sm"
              title="Opens the replace dialog prefilled — one confirming click publishes a tracked version"
              onClick={() => onReplace(c.fact, c.suggested.value, c.key)}
            >
              Replace with “{c.suggested.value.slice(0, 32)}{c.suggested.value.length > 32 ? '…' : ''}”
            </button>
          )}
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => { setKeeping(true); setText(''); }}>
            Both are correct — keep
          </button>
          {canReplace && (
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { setAnswering(true); setText(''); }}>
              Different answer…
            </button>
          )}
        </span>
      )}
      {keeping && (
        <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <input
            className="cs-input" value={text} onChange={(e) => setText(e.target.value)} autoFocus
            placeholder="Optional — why are both right? e.g. “Aug 19 is the Articles filing date, Aug 20 is the signing date”"
          />
          <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
            <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy === 'keep'} onClick={keepBoth}>
              {busy === 'keep' ? 'Saving…' : 'Confirm — keep both'}
            </button>
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setKeeping(false)}>Cancel</button>
          </span>
        </div>
      )}
      {answering && (
        <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <textarea
            className="cs-input" rows={2} value={text} onChange={(e) => setText(e.target.value)} autoFocus
            placeholder={'Exact replacement text (e.g. “HandsOn Global Management, LLC”) — or plain-language instructions (e.g. “use the comma spelling everywhere except the notary block”)'}
          />
          <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
            <button
              type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!text.trim() || !!busy}
              title="Treats the box as the literal new value — every exact occurrence is replaced and published as a tracked version"
              onClick={() => { onReplace(c.fact, text.trim(), c.key); setAnswering(false); setText(''); }}
            >
              Replace with this exact text
            </button>
            <button
              type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!text.trim() || !!busy}
              title="Hands your instructions to the drafting agent — it applies them and publishes a tracked, revertible version"
              onClick={askAgent}
            >
              {busy === 'agent' ? 'Agent working…' : '🤖 Let the AI apply it'}
            </button>
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setAnswering(false)}>Cancel</button>
          </span>
        </div>
      )}
    </div>
  );
}

function FactsPanel({ facts, headingById, canReplace, onReplace, scope, doc, participant }) {
  const groups = useMemo(() => {
    const g = new Map();
    for (const f of facts || []) {
      if (!g.has(f.type)) g.set(f.type, []);
      g.get(f.type).push(f);
    }
    return [...g.entries()];
  }, [facts]);
  const conflicts = useMemo(() => computeFactConflicts(facts, doc?.factReviews), [facts, doc?.factReviews]);
  const open = conflicts.filter((c) => !c.resolution);
  const flaggedValues = new Set(open.map((c) => c.fact.value));

  return (
    <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-4)', maxHeight: 520, overflow: 'auto' }} aria-label="Key facts">
      <div className="u-stack" style={{ '--gap': '1px' }}>
        <strong className="u-xs u-muted">KEY FACTS</strong>
        <span className="u-xs u-muted">
          Parsed from the current draft — repurposing this document for a new entity or deal? Every one of these
          is a candidate for change. Click a section chip to jump; Replace publishes a tracked version.
        </span>
      </div>

      {conflicts.length > 0 && (
        <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          <strong className="u-xs" style={{ letterSpacing: '0.08em', color: open.length ? 'var(--warn)' : undefined }}>
            {open.length ? `⚠️ NEEDS YOUR REVIEW · ${open.length}` : '✅ ALL CONFLICTS RESOLVED'}
          </strong>
          {open.length > 0 && (
            <span className="u-xs u-muted">
              The draft carries values that usually shouldn't coexist. For each one: accept the suggested
              replacement, confirm both are intentional, or give your own answer.
            </span>
          )}
          {conflicts.map((c) => (
            <ConflictCard
              key={c.key} scope={scope} doc={doc} conflict={c} headingById={headingById}
              canReplace={canReplace} onReplace={onReplace} participant={participant}
            />
          ))}
        </div>
      )}

      {!groups.length && <span className="u-small u-muted">No facts parsed yet — the graph rebuilds within a minute of a new version.</span>}
      {groups.map(([type, list]) => (
        <div key={type} className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <strong className="u-xs" style={{ letterSpacing: '0.08em' }}>{FACT_TYPE_LABEL[type] || type} · {list.length}</strong>
          {list.map((f) => (
            <div key={f.value} className="u-split" style={{ alignItems: 'flex-start', gap: 'var(--sp-2)' }}>
              <span className="u-stack" style={{ '--gap': '2px', minWidth: 0 }}>
                <span className="u-small">
                  {flaggedValues.has(f.value) && <span title="Flagged in the review queue above" aria-hidden="true">⚠️ </span>}
                  <strong>{f.value}</strong>{' '}
                  <span className="u-xs u-muted u-tabular">×{f.count}</span>
                </span>
                <span className="u-cluster" style={{ '--gap': 'var(--sp-1)' }}>
                  {(f.clauseIds || []).slice(0, 5).map((id) => (
                    <Link key={id} to={`?clause=${id}`} className="cs-chip u-xs" title={headingById[id] || id}>
                      {(headingById[id] || id).slice(0, 26)}
                    </Link>
                  ))}
                  {(f.clauseIds || []).length > 5 && <span className="u-xs u-muted">+{f.clauseIds.length - 5} more</span>}
                </span>
              </span>
              {canReplace && (
                <button
                  type="button"
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  style={{ flexShrink: 0 }}
                  title="Replace every occurrence across the document — published as a tracked version"
                  onClick={() => onReplace(f)}
                >
                  Replace…
                </button>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// Deterministic replace-everywhere for one fact: exact literal matches across
// the official draft's headings and text, published as a normal immutable
// version with a full per-section manifest (so it redlines, seals, and
// reverts like any other edit). No LLM — surgical string surgery.
function ReplaceFactModal({ scope, doc, official, dealPoints, participant, fact, initialNext, reviewKey, onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const [next, setNext] = useState(initialNext || '');
  const [busy, setBusy] = useState(false);

  const hits = useMemo(() => {
    let n = 0;
    for (const c of official?.clauses || []) {
      n += `${c.heading || ''}\n${c.text || ''}`.split(fact.value).length - 1;
    }
    return n;
  }, [official, fact.value]);

  const submit = async (e) => {
    e.preventDefault();
    const to = next.trim();
    if (!to || to === fact.value) return;
    setBusy(true);
    try {
      const rep = (s) => String(s || '').split(fact.value).join(to);
      const clauses = official.clauses.map((c) => ({ ...c, heading: rep(c.heading), text: rep(c.text) }));
      const ops = [];
      for (let i = 0; i < clauses.length; i += 1) {
        const b = official.clauses[i];
        const a = clauses[i];
        if (a.text !== b.text || a.heading !== b.heading) {
          ops.push({
            op: 'edit', id: a.id,
            before: { heading: b.heading, text: b.text },
            after: { heading: a.heading, text: a.text },
            rationale: `targeted replace: "${fact.value}" → "${to}"`,
          });
        }
      }
      if (!ops.length) throw new Error('No occurrences in the current draft — it may have changed since the facts were parsed');
      await publishVersion(scope, actorOf(user, participant), doc, {
        clauses,
        label: `Replace: ${fact.value} → ${to}`.slice(0, 120),
        note: `Facts-panel targeted replacement across ${ops.length} section${ops.length === 1 ? '' : 's'}`,
        baseVersionId: official.id,
        dealPoints,
        manifest: {
          source: 'manual',
          via: 'facts-panel',
          instruction: `Replace every occurrence of "${fact.value}" with "${to}"`,
          ops,
        },
      });
      // Came from the review queue → record the ruling so the conflict stops
      // being flagged even before the graph rebuilds.
      if (reviewKey) {
        await updateEntity('dealDoc', scope, doc.id, {
          factReviews: {
            [reviewKey]: {
              decision: 'replaced', note: `→ "${to}"`, value: fact.value, suggested: to,
              byName: user?.displayName || user?.email || 'user', at: Date.now(),
            },
          },
        }).catch(() => {});
      }
      toast(`Replaced in ${ops.length} section${ops.length === 1 ? '' : 's'} — published as a new version`, 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
      setBusy(false);
    }
  };

  return (
    <Modal title="Replace across the document" onClose={onClose} maxWidth={560}>
      <form className="u-stack" style={{ '--gap': 'var(--sp-4)' }} onSubmit={submit}>
        <div className="u-small">
          <span className="cs-badge b-info">{FACT_TYPE_LABEL[fact.type] || fact.type}</span>{' '}
          <strong>{fact.value}</strong>
          <span className="u-muted"> — {hits} exact occurrence{hits === 1 ? '' : 's'} in the current draft</span>
        </div>
        <div className="cs-field">
          <label htmlFor="fact-next">Replace with</label>
          <input
            id="fact-next" className="cs-input" value={next} onChange={(e) => setNext(e.target.value)}
            placeholder={fact.type === 'entity' ? 'e.g. Netiva, LLC' : 'the new value'} autoFocus
          />
          <div className="cs-help">
            Exact, case-sensitive matches only — headings and text, across every section. The change publishes
            as a new tracked version (redline, manifest, seal — and each section individually revertible).
          </div>
        </div>
        <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy || !next.trim() || next.trim() === fact.value || hits === 0}>
            {busy ? 'Publishing…' : `Replace ${hits} occurrence${hits === 1 ? '' : 's'}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// The drafting agent — agentic authoring from chat (and voice, which lands
// in the same proposals feed). Every run creates a PENDING proposal with
// structured section ops; nothing touches the draft until a human applies
// it, which publishes a normal immutable version.
// Collapsible tool strip: the document is the star of this page, so heavy
// panels dock into one-line headers. Open state persists per panel.
function CollapsiblePanel({ storageKey, title, hint, badge, forceOpenSignal, guide, tourId, children }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  // e.g. a pending proposal arrives → surface the panel unless the user
  // explicitly collapsed it before.
  useEffect(() => {
    if (!forceOpenSignal) return;
    try {
      if (localStorage.getItem(storageKey) !== '0') setOpen(true);
    } catch { /* private mode */ }
  }, [forceOpenSignal, storageKey]);
  const toggle = () => setOpen((o) => {
    try { localStorage.setItem(storageKey, o ? '0' : '1'); } catch { /* private mode */ }
    return !o;
  });
  return (
    <div className="cs-card u-stack" data-tour={tourId} style={{ '--gap': open ? 'var(--sp-3)' : 0, paddingTop: 'var(--sp-3)', paddingBottom: 'var(--sp-3)' }}>
      <button type="button" className="cs-panel-toggle u-split" onClick={toggle} aria-expanded={open}>
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
          <span className={`cs-chevron${open ? ' open' : ''}`} aria-hidden="true">▸</span>
          <strong className="u-small">{title}</strong>
          {guide && <HelpDot topic={guide} />}
          {badge}
        </span>
        <span className="u-xs u-muted">{hint}</span>
      </button>
      {open && children}
    </div>
  );
}

function AgentPanel({ scope, doc, current, dealPoints, participant }) {
  const { user } = useAuth();
  const toast = useToast();
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState('');
  const [expanded, setExpanded] = useState(null); // proposal id with open preview
  const [mode, setMode] = useState('precision'); // 'precision' | 'vibe'
  const [deselected, setDeselected] = useState({}); // proposalId → Set of op indexes NOT to apply
  const [feedback, setFeedback] = useState('');
  const { docs: proposals } = useEntityList('proposal', { ...scope, dealDoc: doc.id }, { enabled: true });

  const pending = (proposals || [])
    .filter((p) => p.status === 'pending')
    .sort((a, b) => b.createdAt - a.createdAt);

  if (!isAiConfigured()) return null;

  const clauseById = Object.fromEntries((current?.clauses || []).map((c) => [c.id, c]));
  // Backend-held edit lease: block firing a competing edit that the server
  // would reject anyway — the doc subscription clears this live on release.
  const leaseHeld = !!(doc.editLease && (doc.editLease.expiresAt || 0) > Date.now());
  const isOff = (pid, i) => (deselected[pid] || new Set()).has(i);
  const toggleOp = (pid, i) =>
    setDeselected((d) => {
      const s = new Set(d[pid] || []);
      if (s.has(i)) s.delete(i); else s.add(i);
      return { ...d, [pid]: s };
    });

  const run = async (e) => {
    e.preventDefault();
    if (!instruction.trim()) return;
    setBusy('run');
    try {
      const r = await aiDocAgent({ orgId: scope.org, dealId: scope.deal, docId: doc.id, instruction, mode });
      setInstruction('');
      if (r.misplaced) {
        toast(
          r.belongsIn
            ? `This instruction belongs in “${r.belongsIn}”, not this document. ${r.summary || ''}`
            : (r.summary || 'This instruction belongs in another document in the project.'),
          'warn',
          8000
        );
      } else if (r.alreadyDone) {
        toast(
          `Already in the current draft${r.version ? ` (v${r.version})` : ''}${r.evidence ? ` — ${r.evidence}` : `. ${r.summary || ''}`}`,
          'ok',
          10000
        );
      } else if (r.mode === 'vibe') {
        toast(`Done — published v${r.number}: ${r.summary || 'edit applied'}. See Changes for the full manifest.`, 'ok');
      } else {
        setExpanded(r.proposalId);
        toast(`Proposal drafted — ${r.ops.length} change${r.ops.length === 1 ? '' : 's'}. Review the changes list below.`, 'ok');
        if (r.skippedAlready?.length) {
          toast(`Skipped ${r.skippedAlready.length} change${r.skippedAlready.length === 1 ? '' : 's'} already in the draft: ${r.skippedAlready.join('; ')}`, 'warn', 10000);
        }
      }
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy('');
    }
  };

  const apply = async (p) => {
    setBusy(p.id);
    try {
      const selectedOps = p.ops.filter((_, i) => !isOff(p.id, i));
      if (!selectedOps.length) throw new Error('No changes selected — tick at least one, or dismiss the proposal');
      const { clauses, manifestOps } = applyOpsWithManifest(current.clauses, selectedOps);
      const partial = selectedOps.length < p.ops.length;
      const versionId = await publishVersion(scope, actorOf(user, participant), doc, {
        clauses,
        label: `${partial ? `${selectedOps.length} of ${p.ops.length}: ` : ''}${p.summary || 'Drafting agent proposal'}`.slice(0, 120),
        note: p.instruction,
        baseVersionId: current.id,
        dealPoints,
        manifest: {
          source: 'agent',
          via: p.source || 'chat',
          mode: 'precision',
          instruction: p.instruction,
          proposalId: p.id,
          ...(partial ? { appliedOps: selectedOps.length, proposedOps: p.ops.length } : {}),
          ops: manifestOps,
        },
      });
      await updateEntity('proposal', { ...scope, dealDoc: doc.id }, p.id, {
        status: 'applied',
        appliedVersionId: versionId,
        updatedAt: Date.now(),
      });
      toast(partial ? `Applied ${selectedOps.length} of ${p.ops.length} changes — published as a new version` : 'Applied — published as a new version', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy('');
    }
  };

  const dismiss = async (p) => {
    await updateEntity('proposal', { ...scope, dealDoc: doc.id }, p.id, {
      status: 'dismissed',
      updatedAt: Date.now(),
    });
  };

  // Feedback loop: the agent redrafts the proposal with the reviewer's
  // notes; the superseded proposal is dismissed automatically.
  const revise = async (p) => {
    if (!feedback.trim()) return;
    setBusy(`rev-${p.id}`);
    try {
      const r = await aiDocAgent({
        orgId: scope.org, dealId: scope.deal, docId: doc.id, mode: 'precision',
        instruction:
          `${p.instruction}\n\n(Your previous proposal was: "${p.summary}". ` +
          `The reviewer wants revisions: ${feedback.trim()}. ` +
          'Produce a fresh, complete set of ops reflecting the feedback.)',
      });
      if (r.misplaced) {
        toast(
          r.belongsIn
            ? `That revision belongs in “${r.belongsIn}”, not this document.`
            : (r.summary || 'That revision belongs in another document in the project.'),
          'warn',
          8000
        );
        return;
      }
      if (r.alreadyDone) {
        toast(`Already in the current draft${r.version ? ` (v${r.version})` : ''}${r.evidence ? ` — ${r.evidence}` : ''}`, 'ok', 10000);
        return;
      }
      await dismiss(p);
      setFeedback('');
      setExpanded(r.proposalId);
      toast('Revised proposal drafted — review the new changes list.', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy('');
    }
  };

  // Stale proposal (draft moved since it was computed): re-run the agent
  // with the stored instruction against the CURRENT draft and retire the
  // stale one — Apply should never target moved text blind.
  const recompute = async (p) => {
    setBusy(`rec-${p.id}`);
    try {
      const r = await aiDocAgent({
        orgId: scope.org, dealId: scope.deal, docId: doc.id, mode: 'precision',
        instruction: p.instruction,
      });
      if (r.misplaced) {
        toast(r.summary || 'That instruction belongs in another document in the project.', 'warn', 8000);
        return;
      }
      if (r.alreadyDone) {
        await dismiss(p);
        toast(`Already in the current draft${r.version ? ` (v${r.version})` : ''}${r.evidence ? ` — ${r.evidence}` : ''} — the stale proposal was dismissed.`, 'ok', 10000);
        return;
      }
      await dismiss(p);
      setExpanded(r.proposalId);
      toast('Recomputed against the current draft — review the fresh changes list.', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy('');
    }
  };

  // One row in the targeted changes list: checkbox + preview.
  const opPreview = (p, op, i) => {
    const off = isOff(p.id, i);
    const color = op.op === 'add' ? 'var(--ok)' : op.op === 'remove' ? 'var(--danger)' : 'var(--info)';
    const c = clauseById[op.id];
    return (
      <div key={i} className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'flex-start', opacity: off ? 0.45 : 1 }}>
        <input
          type="checkbox"
          checked={!off}
          onChange={() => toggleOp(p.id, i)}
          aria-label={`Include change ${i + 1}`}
          style={{ marginTop: 4 }}
        />
        <div className="u-stack" style={{ '--gap': 'var(--sp-1)', borderLeft: `3px solid ${color}`, paddingLeft: 'var(--sp-3)', flex: 1, minWidth: 0 }}>
          {op.op === 'add' && (
            <>
              <span className="u-small"><span className="cs-badge b-ok">add</span> <strong>{op.section.heading}</strong></span>
              <div className="u-small" style={{ whiteSpace: 'pre-wrap', maxHeight: 180, overflow: 'auto' }}>{op.section.text}</div>
            </>
          )}
          {op.op === 'remove' && (
            <span className="u-small"><span className="cs-badge b-danger">remove</span> <del>{c?.heading || op.id}</del></span>
          )}
          {op.op === 'edit' && (
            <>
              <span className="u-small">
                <span className="cs-badge b-info">edit</span> <strong>{op.heading || c?.heading || op.id}</strong>
              </span>
              {op.text !== undefined && c && (
                <div className="u-small" style={{ maxHeight: 200, overflow: 'auto' }}>
                  <Redline before={c.text} after={op.text} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <CollapsiblePanel
      storageKey="fl.panel.agent"
      title="Drafting agent"
      guide="drafting-agent"
      tourId="agent-panel"
      hint={mode === 'vibe'
        ? 'Vibe: applies immediately — every change recorded in the manifest, revertible after the fact'
        : 'Precision: proposes a changes list — you approve all, some, or send it back'}
      badge={pending.length > 0 ? <span className="cs-badge b-info">{pending.length} proposal{pending.length === 1 ? '' : 's'} awaiting review</span> : null}
      forceOpenSignal={pending.length > 0}
    >
      <form className="u-cluster" style={{ '--gap': 'var(--sp-2)' }} onSubmit={run}>
        <div className="cs-viewmode" role="group" aria-label="Agent mode">
          {[['precision', 'Precision'], ['vibe', 'Vibe']].map(([m, label]) => (
            <button key={m} type="button" className={mode === m ? 'active' : ''} onClick={() => setMode(m)}>
              {label}
            </button>
          ))}
        </div>
        <input
          className="cs-input"
          style={{ flex: 1, minWidth: 220 }}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder='e.g. "Add a 4.4 Error Handling section with a table of error codes" or "Tighten section 2 and fix its numbering"'
        />
        <button
          type="submit"
          className="cs-btn cs-btn-primary cs-btn-sm"
          disabled={busy === 'run' || !instruction.trim() || leaseHeld}
          title={leaseHeld ? 'Another AI task is editing this document — it frees up when that task finishes' : undefined}
        >
          {leaseHeld
            ? '🔒 Editing in progress'
            : busy === 'run'
              ? (mode === 'vibe' ? 'Doing it…' : 'Drafting…')
              : (mode === 'vibe' ? 'Do it' : 'Draft it')}
        </button>
      </form>

      {pending.map((p) => {
        const selectedCount = p.ops.length - (deselected[p.id]?.size || 0);
        return (
          <div key={p.id} className="cs-banner b-info u-stack" style={{ '--gap': 'var(--sp-2)' }}>
            <div className="u-split">
              <span className="u-small">
                <strong>{p.summary || p.instruction.slice(0, 100)}</strong>{' '}
                <span className="u-xs u-muted">
                  · {p.ops.length} change{p.ops.length === 1 ? '' : 's'} · via {p.source} · {p.requestedByName}
                </span>
              </span>
              <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0 }}>
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
                  {expanded === p.id ? 'Hide changes' : 'Review changes'}
                </button>
                <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy === p.id || !selectedCount} onClick={() => apply(p)}>
                  {busy === p.id ? 'Applying…'
                    : selectedCount === p.ops.length ? 'Apply as new version'
                      : `Apply ${selectedCount} of ${p.ops.length}`}
                </button>
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => dismiss(p)}>Dismiss</button>
              </span>
            </div>
            {p.baseVersionId && doc.currentVersionId && p.baseVersionId !== doc.currentVersionId && (
              <div className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
                <span className="cs-badge b-warn">stale</span>
                <span className="u-xs" style={{ color: 'var(--warn, #b45309)' }}>
                  Drafted against an earlier version — the draft has moved since.
                </span>
                <button
                  type="button"
                  className="cs-btn cs-btn-outline cs-btn-sm"
                  disabled={busy === `rec-${p.id}` || leaseHeld}
                  data-tip="Re-runs the agent with this proposal's instruction against the current draft, then retires this stale one"
                  onClick={() => recompute(p)}
                >
                  {busy === `rec-${p.id}` ? 'Recomputing…' : 'Recompute against current draft'}
                </button>
              </div>
            )}
            {expanded === p.id && (
              <>
                <div className="u-stack" style={{ '--gap': 'var(--sp-3)', maxHeight: 420, overflow: 'auto' }}>
                  {p.ops.map((op, i) => opPreview(p, op, i))}
                </div>
                <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
                  <input
                    className="cs-input"
                    style={{ flex: 1, minWidth: 200 }}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder='Not quite right? e.g. "Keep the table but make the new section shorter and move it after 5.2"'
                  />
                  <button
                    type="button"
                    className="cs-btn cs-btn-outline cs-btn-sm"
                    disabled={busy === `rev-${p.id}` || !feedback.trim()}
                    onClick={() => revise(p)}
                  >
                    {busy === `rev-${p.id}` ? 'Revising…' : 'Revise proposal'}
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })}
    </CollapsiblePanel>
  );
}

// Header badge for the document's catalog type; click to re-type via the
// picker. Surfaces smart-import's mapping (or its proposal for a new org
// type) until the user confirms or picks something else.
function DocTypeBadge({ scope, doc, canEdit }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const { catalog, byKey } = useDocTypeCatalog(scope?.org);
  const match = doc.aiProfile?.docTypeMatch;
  const userTyped = doc.docType && !['agreement', 'design', 'general', 'deck', 'workbook'].includes(doc.docType);
  const suggestion = !userTyped && (match ? { entry: byKey.get(match.key) || null, confidence: match.confidence } : doc.aiProfile?.docTypeProposal ? { proposed: doc.aiProfile.docTypeProposal } : null);
  const save = async (key, entry) => {
    if (!key) { setEditing(false); return; }
    try {
      await updateEntity('dealDoc', scope, doc.id, { docType: key, docView: entry?.view || docViewOf({ docType: key, format: doc.format }) });
      toast(`Typed as ${entry?.label || key}`, 'ok');
    } catch (err) { toast(err.message, 'danger'); }
    setEditing(false);
  };
  const showSuggestion = !!(suggestion?.entry || suggestion?.proposed) && canEdit && !userTyped;
  if (editing || showSuggestion) {
    return (
      <span style={{ display: 'inline-block', minWidth: 260, verticalAlign: 'middle' }} data-nova-note="Document type: pick from the catalog or add a new type">
        <DocTypePicker compact orgId={scope?.org} value={userTyped ? doc.docType : ''} onChange={save} suggestion={editing ? null : suggestion} />
      </span>
    );
  }
  const label = docTypeLabel(doc, catalog);
  return canEdit ? (
    <button type="button" className="cs-chip" style={{ cursor: 'pointer' }} onClick={() => setEditing(true)} data-tip="Change the document type" aria-label={`Document type: ${label}. Change`}>
      {label}
    </button>
  ) : <span className="cs-chip">{label}</span>;
}

function AiPanel({ orgId, dealId, docId, doc, versions, participant }) {
  const toast = useToast();
  const [output, setOutput] = useState(null); // { title, text }
  const [busy, setBusy] = useState('');

  const partyKey = participant?.partyKey || '';

  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === 'summary') {
        const r = await aiSummarize({ orgId, dealId, docId, partyKey });
        setOutput({ title: `AI summary${partyKey ? ` — ${partyKey} perspective` : ''}`, text: r.summary });
      } else if (kind === 'risks') {
        const r = await aiRisks({ orgId, dealId, docId });
        setOutput({ title: 'AI risk & consistency scan', text: r.risks });
      } else if (kind === 'compare') {
        const sorted = [...(versions || [])].sort((a, b) => a.number - b.number);
        if (sorted.length < 2) throw new Error('Need at least two versions to compare');
        const r = await aiCompare({
          orgId,
          dealId,
          docId,
          fromVersionId: sorted[sorted.length - 2].id,
          toVersionId: sorted[sorted.length - 1].id,
          partyKey,
        });
        setOutput({ title: `What changed (v${sorted[sorted.length - 2].number} → v${sorted[sorted.length - 1].number})`, text: r.analysis });
      }
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy('');
    }
  };

  if (!isAiConfigured()) return null;

  return (
    <CollapsiblePanel
      storageKey="fl.panel.ai"
      title="AI assistance"
      guide="brain"
      hint="Summaries, risk scan, what changed — suggestions only, never applied without you"
    >
      <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
        <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!busy} onClick={() => run('summary')}>
          {busy === 'summary' ? 'Summarizing…' : 'Summarize for my party'}
        </button>
        <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!busy} onClick={() => run('risks')}>
          {busy === 'risks' ? 'Scanning…' : 'Risk scan'}
        </button>
        <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!busy || (versions || []).length < 2} onClick={() => run('compare')}>
          {busy === 'compare' ? 'Analyzing…' : 'What changed?'}
        </button>
      </div>
      {doc.aiProfile?.summary && !output && (
        <div className="u-small u-muted">
          <strong>Smart import:</strong> {doc.aiProfile.docType}
          {doc.aiProfile.governingLaw ? ` · ${doc.aiProfile.governingLaw}` : ''} — {doc.aiProfile.summary}
        </div>
      )}
      {output && (
        <div className="cs-banner b-info u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <div className="u-split">
            <strong className="u-small">{output.title}</strong>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setOutput(null)} aria-label="Close AI output">✕</button>
          </div>
          <div className="u-small" style={{ whiteSpace: 'pre-wrap' }}>{output.text}</div>
        </div>
      )}
    </CollapsiblePanel>
  );
}

// Everything the knowledge graph knows about one clause — what it defines,
// what it relies on, who points at it, and what is semantically entangled
// with it. This is the "don't lose precision" guardrail: shown as a panel on
// the clause and as a warning inside the editor.
export function clauseImpact(graph, clauseId) {
  if (!graph) return null;
  const defines = (graph.terms || []).filter((t) => t.definedIn === clauseId);
  const usesTerms = (graph.terms || []).filter((t) => t.definedIn !== clauseId && (t.usedIn || []).includes(clauseId));
  const references = (graph.refs || []).filter((r) => r.from === clauseId);
  const referencedBy = (graph.refs || []).filter((r) => r.to === clauseId);
  const related = (graph.related || []).flatMap((r) =>
    r.a === clauseId ? [{ id: r.b, score: r.score }] : r.b === clauseId ? [{ id: r.a, score: r.score }] : []
  );
  const rippleCount =
    defines.reduce((s, t) => s + (t.usedIn || []).length, 0) + referencedBy.length;
  return { defines, usesTerms, references, referencedBy, related, rippleCount };
}

// ---- Document pagination ----------------------------------------------------
// Long agreements render progressively (infinite scroll): fixed-size chunks
// of sections mount as the reader approaches the end of what's loaded, so the
// document reads as one continuous Word-like sheet. The bar shows the live
// reading position + a jump field; the thumbnails menu (expandable) renders each page
// as a miniature sheet — first headings + text-line hints — for visual seeking.
const DOC_PAGE_SIZE = 10;

function DocPager({ page, pageCount, loaded, onPage, showThumbs, onToggleThumbs }) {
  const [jump, setJump] = useState('');
  if (pageCount <= 1) return null;
  return (
    <div className="cs-pager">
      <span className="u-small u-muted u-tabular" title="Reading position — the document keeps loading as you scroll">
        Page {page} of {pageCount}{loaded < pageCount ? ` · ${loaded} loaded` : ''}
      </span>
      <input
        className="cs-input cs-pager-jump"
        placeholder="Go…"
        value={jump}
        onChange={(e) => setJump(e.target.value.replace(/\D/g, ''))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && jump) {
            onPage(Math.min(pageCount, Math.max(1, Number(jump))));
            setJump('');
          }
        }}
        aria-label={`Jump to page (1–${pageCount})`}
      />
      <button
        className={`cs-btn cs-btn-sm ${showThumbs ? 'cs-btn-primary' : 'cs-btn-outline'}`}
        onClick={onToggleThumbs}
        aria-pressed={showThumbs}
        data-tip="Page thumbnails"
      >
        ▦ Pages
      </button>
    </div>
  );
}

function PageThumbs({ clauses, page, onPage }) {
  const pages = [];
  for (let i = 0; i < clauses.length; i += DOC_PAGE_SIZE) pages.push(clauses.slice(i, i + DOC_PAGE_SIZE));
  return (
    <div className="cs-page-thumbs" role="navigation" aria-label="Page thumbnails">
      {pages.map((pc, i) => (
        <button key={i} className={`cs-page-thumb ${page === i + 1 ? 'is-current' : ''}`} onClick={() => onPage(i + 1)}>
          <span className="cs-page-thumb-paper">
            {pc.slice(0, 4).map((c) => (
              <span key={c.id} style={{ display: 'contents' }}>
                <span className="cs-page-thumb-h">{c.heading || '§'}</span>
                {[...Array(Math.min(4, Math.max(1, Math.round((c.text || '').length / 400))))].map((_, j) => (
                  <span key={j} className="cs-page-thumb-line" style={{ width: `${100 - (j === 3 ? 35 : j * 8)}%` }} />
                ))}
              </span>
            ))}
          </span>
          <span className="cs-page-thumb-no">{i + 1}</span>
        </button>
      ))}
    </div>
  );
}

// Compact link to another clause in the same document (deep-links + scrolls).
function ClauseLink({ id, headingById, extra }) {
  return (
    <Link className="cs-chip" to={`?clause=${id}`} style={{ textDecoration: 'none' }}>
      {(headingById[id] || id).slice(0, 44)}{extra ? ` ${extra}` : ''}
    </Link>
  );
}

function DependenciesPanel({ impact, headingById }) {
  const row = (label, children) => (
    <div className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
      <span className="u-xs u-muted" style={{ minWidth: 110, flexShrink: 0 }}>{label}</span>
      <span className="u-cluster" style={{ '--gap': 'var(--sp-1)' }}>{children}</span>
    </div>
  );
  return (
    <div className="u-stack u-small" style={{ '--gap': 'var(--sp-2)', borderTop: '1px solid var(--border)', paddingTop: 'var(--sp-3)' }}>
      {impact.defines.length > 0 &&
        row('Defines', impact.defines.map((t) => (
          <span key={t.term} className="cs-chip" data-tip={`Used in ${t.usedIn.length} other clause${t.usedIn.length === 1 ? '' : 's'}`}>
            “{t.term}” ×{t.usedIn.length}
          </span>
        )))}
      {impact.defines.some((t) => t.usedIn.length > 0) &&
        row('Term used in', [...new Set(impact.defines.flatMap((t) => t.usedIn))].slice(0, 12).map((id) => (
          <ClauseLink key={id} id={id} headingById={headingById} />
        )))}
      {impact.usesTerms.length > 0 &&
        row('Relies on', impact.usesTerms.slice(0, 12).map((t) => (
          <ClauseLink key={t.term} id={t.definedIn} headingById={headingById} extra={`(“${t.term}”)`} />
        )))}
      {impact.referencedBy.length > 0 &&
        row('Referenced by', impact.referencedBy.map((r, i) => (
          <ClauseLink key={`${r.from}-${i}`} id={r.from} headingById={headingById} />
        )))}
      {impact.references.length > 0 &&
        row('References', impact.references.map((r, i) => (
          <ClauseLink key={`${r.to}-${i}`} id={r.to} headingById={headingById} extra={`(${r.label})`} />
        )))}
      {impact.related.length > 0 &&
        row('Similar language', impact.related.map((r) => (
          <ClauseLink key={r.id} id={r.id} headingById={headingById} extra={`(${Math.round(r.score * 100)}%)`} />
        )))}
    </div>
  );
}

function ClauseEditor({ scope, doc, version, clause, dealPoints, participant, impact, headingById, onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const [text, setText] = useState(clause.text);
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiOut, setAiOut] = useState('');
  const [busy, setBusy] = useState('');

  // Locked deal points referencing this clause freeze its language.
  const lockedRefs = (dealPoints || []).filter(
    (p) => p.locked && (p.clauseRefs || []).some((r) => r.docId === doc.id && r.clauseId === clause.id)
  );

  const publish = async (e) => {
    e.preventDefault();
    if (lockedRefs.length) {
      toast(`Clause is frozen by locked deal point: ${lockedRefs[0].title}`, 'danger');
      return;
    }
    setBusy('publish');
    try {
      const clauses = version.clauses.map((c) => (c.id === clause.id ? { ...c, text } : c));
      await publishVersion(scope, actorOf(user, participant), doc, {
        clauses,
        label: label || `Edit: ${clause.heading}`,
        note,
        baseVersionId: version.id,
        dealPoints,
        manifest: {
          source: 'manual',
          instruction: note || label || `Edit: ${clause.heading}`,
          ops: [{
            op: 'edit',
            id: clause.id,
            before: { heading: clause.heading, text: clause.text },
            after: { heading: clause.heading, text },
          }],
        },
      });
      toast('New version published', 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy('');
    }
  };

  const askAi = async () => {
    if (!aiInstruction.trim()) return;
    setBusy('ai');
    try {
      const r = await aiDraft({
        orgId: scope.org,
        dealId: scope.deal,
        docId: doc.id,
        clauseId: clause.id,
        instruction: aiInstruction,
      });
      setAiOut(r.draft);
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy('');
    }
  };

  return (
    <Modal title={`Propose changes — ${clause.heading}`} onClose={onClose} maxWidth={860}>
      <form className="u-stack" style={{ '--gap': 'var(--sp-4)' }} onSubmit={publish}>
        {lockedRefs.length > 0 && (
          <div className="cs-banner b-warn">
            This clause is frozen by a locked deal point ({lockedRefs.map((p) => p.title).join(', ')}). Unlock it first.
          </div>
        )}
        {impact && impact.rippleCount > 0 && (
          <div className="cs-banner b-info u-small">
            <strong>Ripple check:</strong>{' '}
            {impact.defines.filter((t) => t.usedIn.length > 0).map((t) => `defines “${t.term}” used in ${t.usedIn.length} other clause${t.usedIn.length === 1 ? '' : 's'}`).join('; ')}
            {impact.defines.some((t) => t.usedIn.length > 0) && impact.referencedBy.length > 0 ? '; ' : ''}
            {impact.referencedBy.length > 0 &&
              `cross-referenced by ${impact.referencedBy.map((r) => headingById[r.from] || r.from).slice(0, 4).join(', ')}${impact.referencedBy.length > 4 ? '…' : ''}`}
            . Keep those consistent with this edit.
          </div>
        )}
        <div className="cs-field">
          <label htmlFor="clause-text">Clause text</label>
          <textarea
            id="clause-text"
            className="cs-input"
            style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 'var(--fs-sm)' }}
            rows={14}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        {text !== clause.text && (
          <div className="cs-card u-small" style={{ maxHeight: 220, overflow: 'auto' }}>
            <strong className="u-xs u-muted">Redline preview</strong>
            <div style={{ marginTop: 'var(--sp-2)' }}>
              <Redline before={clause.text} after={text} />
            </div>
          </div>
        )}

        {isAiConfigured() && (
          <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-2)' }}>
            <strong className="u-small">AI drafting help (respects agreed & locked deal points)</strong>
            <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
              <input
                className="cs-input"
                style={{ flex: 1, minWidth: 200 }}
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                placeholder="e.g. Cap indemnity at 10% of purchase price, 18-month survival"
              />
              <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy === 'ai'} onClick={askAi}>
                {busy === 'ai' ? 'Drafting…' : 'Suggest redraft'}
              </button>
            </div>
            {aiOut && (
              <div className="cs-banner b-info u-stack" style={{ '--gap': 'var(--sp-2)' }}>
                <div className="u-small" style={{ whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto' }}>{aiOut}</div>
                <div className="u-cluster">
                  <button
                    type="button"
                    className="cs-btn cs-btn-outline cs-btn-sm"
                    onClick={() => {
                      // Take the drafted clause body (strip the trailing note line if present).
                      setText(aiOut.replace(/^1\)\s*/m, '').split(/\n2\)\s/)[0].trim());
                    }}
                  >
                    Use as clause text
                  </button>
                  <span className="u-xs u-muted">AI suggestion — review before publishing</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="u-cluster" style={{ '--gap': 'var(--sp-3)' }}>
          <div className="cs-field" style={{ flex: 1 }}>
            <label htmlFor="v-label">Version label</label>
            <input id="v-label" className="cs-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`Edit: ${clause.heading}`} />
          </div>
          <div className="cs-field" style={{ flex: 1 }}>
            <label htmlFor="v-note">Cover note</label>
            <input id="v-note" className="cs-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this change" />
          </div>
        </div>
        <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy === 'publish' || text === clause.text || lockedRefs.length > 0}>
            Publish as new version
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Checkpoint-chain verification report (deterministic — recomputes every
// version's hash server-side and reconciles Firestore vs the S3 archives).
function VerifyModal({ orgId, dealId, docId, onClose }) {
  const toast = useToast();
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(true);

  React.useEffect(() => {
    let alive = true;
    verifyChain({ orgId, dealId, docId })
      .then((r) => alive && setReport(r))
      .catch((err) => {
        toast(err.message, 'danger');
        onClose();
      })
      .finally(() => alive && setBusy(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mark = (ok) =>
    ok === null ? <span className="u-muted">—</span> : ok ? <span style={{ color: 'var(--ok)' }}>pass</span> : <span style={{ color: 'var(--danger)' }}>FAIL</span>;

  return (
    <Modal title="Contract integrity verification" onClose={onClose} maxWidth={760}>
      {busy && <div className="cs-skeleton" style={{ height: 120 }} />}
      {report && (
        <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
          <div className={`cs-banner ${report.chainIntact ? 'b-ok' : 'b-warn'}`} role="status">
            {report.chainIntact
              ? `Chain intact — all ${report.count} versions verified against their sealed checkpoints and S3 archives.`
              : 'Chain not fully verified — unsealed or failing versions below.'}
          </div>
          <div className="u-scroll-x">
            <table className="cs-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Sealed</th>
                  <th>Content</th>
                  <th>Chain</th>
                  <th>Archive</th>
                  <th>Checkpoint</th>
                </tr>
              </thead>
              <tbody>
                {report.versions.map((v) => (
                  <tr key={v.versionId}>
                    <td>v{v.number}{v.label ? ` — ${v.label}` : ''}</td>
                    <td>{v.sealed ? new Date(v.sealedAt).toLocaleString() : 'pending'}</td>
                    <td>{mark(v.contentOk)}</td>
                    <td>{mark(v.chainOk)}</td>
                    <td>{mark(v.archiveOk)}</td>
                    <td className="u-xs u-tabular u-muted">{v.chainHash ? `${v.chainHash.slice(0, 16)}…` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="u-xs u-muted" style={{ margin: 0 }}>
            Content = clause text matches the sealed hash. Chain = each checkpoint correctly references its predecessor.
            Archive = the immutable S3 copy matches. Any tampering after sealing fails all later links.
          </p>
        </div>
      )}
    </Modal>
  );
}

// The explanation rail: every change between the compared versions as a card
// in DOCUMENT order (not time order) — what changed, in which version, and
// the recorded reasoning behind it. Scroll-synced with the redline: reading a
// change highlights its card; clicking a card jumps to the section.
function ExplainRail({ cards, activeId, onJump, baseLabel, currentLabel }) {
  const railRef = useRef(null);
  useEffect(() => {
    if (!activeId) return;
    railRef.current
      ?.querySelector(`[data-cid="${activeId}"]`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeId]);
  const fmt = (t) => new Date(t).toLocaleDateString([], { month: 'short', day: 'numeric' });
  return (
    <aside ref={railRef} className="cs-explain-rail u-stack" style={{ '--gap': 'var(--sp-2)' }} aria-label="Why each change">
      <div className="u-stack" style={{ '--gap': '2px', paddingBottom: 'var(--sp-1)' }}>
        <strong className="u-small">🧠 Why each change · {cards.length}</strong>
        <span className="u-xs u-muted">{currentLabel} vs {baseLabel}, in document order — click a card to jump to that section</span>
      </div>
      {cards.map((c) => {
        const active = c.id === activeId;
        return (
          <button
            key={c.id}
            type="button"
            data-cid={c.id}
            onClick={() => onJump(c.id)}
            className="cs-card u-stack"
            style={{
              '--gap': '4px',
              padding: 'var(--sp-2) var(--sp-3)',
              textAlign: 'left',
              cursor: 'pointer',
              font: 'inherit',
              color: 'inherit',
              ...(active ? { borderColor: 'var(--brand)', boxShadow: '0 0 0 1px var(--brand)' } : {}),
            }}
          >
            <span className="u-small" style={{ lineHeight: 1.35 }}>
              <strong>
                {c.removed ? '➖ ' : c.added ? '➕ ' : '✏️ '}
                {c.heading || c.id}
              </strong>
            </span>
            {c.notes.length > 0
              ? c.notes.map((n, i) => (
                <span key={i} className="u-xs" style={{ lineHeight: 1.5 }}>
                  <span className="u-muted u-tabular">v{n.v} · {n.via} · {fmt(n.when)}</span>
                  {' — '}{n.why || 'no note recorded'}
                </span>
              ))
              : (
                <span className="u-xs u-muted">
                  {c.editors.length
                    ? `Changed in ${c.editors.map((e) => `v${e.number} by ${e.who}`).join(', then ')} — no rationale was recorded.`
                    : 'No recorded rationale for this change.'}
                </span>
              )}
          </button>
        );
      })}
    </aside>
  );
}

// One clause in any view mode. 'clean' = current text; 'redline' = inline
// diff vs the base; 'sxs' = base clean on the left, redline on the right.
// Changed clauses get a left border in the editing party's color.
function ClauseCard({ scope, docId, clause, anchored, base, mode, partyColor, editors, targeted, participant, impact, headingById, deck, slideNo, onEdit, removed }) {
  const [showComments, setShowComments] = useState(false);
  const [showDeps, setShowDeps] = useState(false);
  const baseText = base ? base.text : '';
  // A removed section exists only in the base — render its full text as a
  // deletion (redline vs empty).
  const afterText = removed ? '' : clause.text;
  const changed = mode !== 'clean' && baseText !== afterText;
  // Who touched this clause between the base and the viewed version — shown
  // on hover over every insertion/deletion and on the "changed" chip.
  const editTip = (editors || [])
    .map((e) => `v${e.number} — ${e.who}${e.party ? ` (${e.party})` : ''} · ${e.when}`)
    .join(', then ');
  const depCount = impact
    ? impact.defines.length + impact.usesTerms.length + impact.references.length + impact.referencedBy.length + impact.related.length
    : 0;
  const level = clause.level || 1;
  return (
    <div
      id={`clause-${clause.id}`}
      className={`cs-card u-stack ${changed ? 'cs-clause-changed' : ''} ${targeted ? 'cs-clause-target' : ''}`}
      style={{
        '--gap': 'var(--sp-2)',
        '--party-color': partyColor,
        marginLeft: (level - 1) * 20,
      }}
    >
      <div className="u-split">
        <strong style={{
          ...(level > 1 ? { fontSize: level === 2 ? 'var(--fs-sm)' : 'var(--fs-xs)' } : {}),
          ...(removed ? { textDecoration: 'line-through', opacity: 0.65 } : {}),
        }}
        >
          {deck && <span className="cs-chip u-tabular" style={{ marginRight: 'var(--sp-2)' }}>Slide {slideNo}{clause.layout && clause.layout !== 'content' ? ` · ${clause.layout}` : ''}</span>}
          {clause.heading}
        </strong>
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0 }}>
          {removed && (
            <span className="cs-chip" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>
              removed
            </span>
          )}
          {changed && !removed && (
            <span
              className="cs-chip"
              style={{ borderColor: partyColor, color: partyColor, cursor: editTip ? 'help' : undefined }}
              data-tip={editTip || undefined}
            >
              changed{editors?.length ? ` · ${editors[editors.length - 1].who}` : ''}
            </span>
          )}
          {anchored.map((p) => {
            // Decided points stay anchored but recede: dimmed chip + the
            // decision as a badge, so the rail reads "what's still live".
            const decision = decisionOf(p);
            const decided = decision === 'accepted' || decision === 'rejected' || decision === 'fallenAway';
            return (
              <span
                key={p.id}
                className="cs-chip"
                style={decided ? { opacity: 0.55 } : undefined}
                data-tip={`${p.title} (${DP_DECISION_LABELS[decision] || decision}${p.locked ? ', locked' : ''})`}
              >
                {decided ? `${DP_DECISION_LABELS[decision]} · ` : ''}{p.locked ? 'locked · ' : ''}{p.title.slice(0, 32)}{p.title.length > 32 ? '…' : ''}
              </span>
            );
          })}
          {depCount > 0 && (
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={() => setShowDeps((s) => !s)}
              aria-expanded={showDeps}
              data-tip="Defined terms, cross-references and related clauses (knowledge graph)"
            >
              Links · {depCount}
            </button>
          )}
          {!removed && (
            <>
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                onClick={() => setShowComments((s) => !s)}
                aria-expanded={showComments}
              >
                Comments
              </button>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onEdit}>
                Propose changes
              </button>
            </>
          )}
        </span>
      </div>

      {mode === 'sxs' ? (
        <div className="cs-sxs-stack">
          {alignSxsRows(baseText, afterText).map((row, i) => (
            <div key={i} className="cs-sxs cs-sxs-row">
              <div className="u-small cs-clause-text" style={{ lineHeight: 1.6 }}>
                {row.left != null
                  ? <ClauseText text={row.left} />
                  : !baseText
                    ? <span className="u-muted">— not present in this version —</span>
                    : null}
              </div>
              <div className="u-small cs-clause-text" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {row.left === row.right
                  ? <ClauseText text={row.right} />
                  : <Redline before={row.left || ''} after={row.right || ''} insColor={partyColor} tip={editTip} />}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="u-small cs-clause-text" style={{ whiteSpace: mode === 'redline' ? 'pre-wrap' : undefined, lineHeight: 1.6 }}>
          {mode === 'redline'
            ? <Redline before={baseText} after={afterText} insColor={partyColor} tip={editTip} />
            : deck ? <SlideBody text={clause.text} /> : <ClauseText text={clause.text} />}
        </div>
      )}

      {showDeps && impact && <DependenciesPanel impact={impact} headingById={headingById} />}

      {showComments && (
        <CommentThread
          scope={scope}
          participant={participant}
          anchor={{ kind: 'clause', docId, clauseId: clause.id }}
          compact
        />
      )}
    </div>
  );
}

export default function DocView() {
  const { dealId, docId } = useParams();
  const { orgId } = useOrg();
  const { user } = useAuth();
  const navigate = useNavigate();
  const scope = orgId ? { org: orgId, deal: dealId } : null;
  const { can } = useDealPowers(dealId);

  const { doc: deal } = useEntityDoc('deal', orgId ? { org: orgId } : null, orgId ? dealId : null);
  const { doc } = useEntityDoc('dealDoc', scope, scope ? docId : null);
  // Sibling docs power the lineage strip: what this doc was derived from,
  // which draft it replaced, and (if it's history) what replaced it.
  const { docs: siblingDocs } = useEntityList('dealDoc', scope, { enabled: !!scope });
  const { docs: versions } = useEntityList('version', scope ? { ...scope, dealDoc: docId } : null, { enabled: !!scope });
  const { docs: dealPoints } = useEntityList('dealPoint', scope, { enabled: !!scope });
  const { docs: participants } = useEntityList('participant', scope, { enabled: !!scope });
  // Knowledge graph of the current version (built server-side): defined
  // terms, cross-references, similarity edges — powers the Links panels.
  // Big documents spill the node arrays to S3 (Firestore doc keeps stats +
  // s3Key) — fetch the full JSON through the presigned-download path.
  const { doc: graphDoc } = useEntityDoc('docGraph', scope ? { ...scope, dealDoc: docId } : null, scope ? 'current' : null);
  const [spilledGraph, setSpilledGraph] = useState(null);
  useEffect(() => {
    let alive = true;
    setSpilledGraph(null);
    if (graphDoc?.s3Key && !graphDoc.terms) {
      getAssetDownloadUrl({ key: graphDoc.s3Key })
        .then((url) => fetch(url))
        .then((r) => r.json())
        .then((g) => { if (alive) setSpilledGraph(g); })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, [graphDoc?.s3Key, graphDoc?.builtAt, graphDoc?.terms]);
  const graph = graphDoc?.s3Key && !graphDoc.terms ? spilledGraph : graphDoc;

  // Deep links: /deals/:d/docs/:id?mode=redline|sxs|clean&clause=<clauseId>&v=<versionId>
  const [searchParams] = useSearchParams();
  const [viewVersionId, setViewVersionId] = useState(() => searchParams.get('v') || null);
  const [compareToId, setCompareToId] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    const m = searchParams.get('mode');
    return ['clean', 'redline', 'sxs'].includes(m) ? m : 'clean';
  });
  // A tracked-changes file's content IS the markup (v1 = rejected, v2 =
  // accepted) — landing on "clean" would show no redlines at all. Default to
  // redline unless the deep link asked for a specific mode.
  const trackedDefaultApplied = useRef(false);
  useEffect(() => {
    if (doc?.trackedChanges && !searchParams.get('mode') && !trackedDefaultApplied.current) {
      trackedDefaultApplied.current = true;
      setViewMode('redline');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.trackedChanges]);
  const [partyFilter, setPartyFilter] = useState('');
  const [paper, setPaper] = useState(true); // Word-like page rendering
  const [showOutline, setShowOutline] = useState(false);
  // ?facts=1 deep-links straight into the facts panel (dashboard audit card).
  const [showFacts, setShowFacts] = useState(() => searchParams.get('facts') === '1');
  const [replaceFact, setReplaceFact] = useState(null);
  const [changesOnly, setChangesOnly] = useState(false);
  // Progressive rendering (infinite scroll): long documents mount
  // DOC_PAGE_SIZE sections at a time; scrolling near the end of what's loaded
  // auto-appends the next chunk, so the sheet reads continuously.
  // renderedPages = chunks mounted · scrollChunk = live reading position ·
  // pendingChunk = a jump target waiting for its chunk to mount.
  const [renderedPages, setRenderedPages] = useState(1);
  const [scrollChunk, setScrollChunk] = useState(1);
  const [pendingChunk, setPendingChunk] = useState(0);
  const loadMoreRef = useRef(null);
  const [showThumbs, setShowThumbs] = useState(false);
  // Explanation rail (diff modes): document-order reasoning beside the
  // redline. activeClauseId = scrollspy highlight; focusClauseId = card click.
  const [showExplain, setShowExplain] = useState(true);
  const [activeClauseId, setActiveClauseId] = useState('');
  const [focusClauseId, setFocusClauseId] = useState('');
  const targetClauseId = searchParams.get('clause') || '';
  const [editingClause, setEditingClause] = useState(null);
  const [showVerify, setShowVerify] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const [memoBusy, setMemoBusy] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [showNewDraft, setShowNewDraft] = useState(false);
  const [exporting, setExporting] = useState('');
  const toast = useToast();

  const participant = (participants || []).find((p) => p.uid === user?.uid) || null;
  // Large versions spill their clause arrays to S3 — hydrate them into a map
  // keyed by version id so every downstream consumer sees full clauses.
  const [spilledById, setSpilledById] = useState({});
  useEffect(() => {
    let alive = true;
    for (const v of versions || []) {
      if (v.clausesS3Key && !spilledById[v.id]) {
        loadVersionClauses(v)
          .then((clauses) => { if (alive) setSpilledById((m) => ({ ...m, [v.id]: clauses })); })
          .catch(() => {});
      }
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions]);
  const sorted = useMemo(
    () =>
      [...(versions || [])]
        .map((v) => (v.clausesS3Key ? { ...v, clauses: spilledById[v.id] || [] } : v))
        .sort((a, b) => a.number - b.number),
    [versions, spilledById]
  );
  const current = sorted.find((v) => v.id === (viewVersionId || doc?.currentVersionId)) || sorted[sorted.length - 1];
  // Comparison base: explicit pick, else the version this one was edited
  // from, else the previous version by number.
  const autoBase = current
    ? sorted.find((v) => v.id === current.baseVersionId) || sorted.filter((v) => v.number < current.number).pop() || null
    : null;
  const compareTo = (compareToId ? sorted.find((v) => v.id === compareToId) : null) || autoBase;

  const parties = deal?.parties || [];
  const partyColors = Object.fromEntries(parties.map((p, i) => [p.key, PARTY_COLORS[i % PARTY_COLORS.length]]));
  const partyLabel = (key) => parties.find((p) => p.key === key)?.label || key;
  // Versions filtered by negotiating side (for both selectors).
  const versionOptions = partyFilter ? sorted.filter((v) => v.partyKey === partyFilter) : sorted;
  const currentPartyColor = current ? (partyColors[current.partyKey] || 'var(--info)') : 'var(--info)';

  // Deep-linked clause: mount every chunk up to it first, then scroll it
  // into view.
  useEffect(() => {
    if (!targetClauseId || !current) return;
    const idx = (current.clauses || []).findIndex((c) => c.id === targetClauseId);
    if (idx >= 0) {
      const needed = Math.floor(idx / DOC_PAGE_SIZE) + 1;
      if (needed > renderedPages) {
        setRenderedPages(needed);
        return; // re-runs after the chunks mount, then scrolls
      }
    }
    const el = document.getElementById(`clause-${targetClauseId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetClauseId, current?.id, renderedPages]);

  // Scrollspy for the explanation rail: the topmost clause in the reading
  // band drives which rationale card is highlighted.
  useEffect(() => {
    if (viewMode === 'clean' || !showExplain) { setActiveClauseId(''); return undefined; }
    const els = Array.from(document.querySelectorAll('[id^="clause-"]'));
    if (!els.length) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const hit = entries.find((e) => e.isIntersecting);
        if (hit) setActiveClauseId(hit.target.id.replace('clause-', ''));
      },
      { rootMargin: '-10% 0px -70% 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, showExplain, renderedPages, changesOnly, current?.id, compareTo?.id, sorted]);

  // Rail card click: scroll the clause into view (runs again after its chunk
  // mounts and the target renders).
  useEffect(() => {
    if (!focusClauseId) return;
    document.getElementById(`clause-${focusClauseId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusClauseId, renderedPages]);

  const exportWord = async (exportMode, format = 'docx') => {
    setExporting(`${exportMode}-${format}`);
    try {
      await downloadWordExport({
        orgId, dealId, docId,
        versionId: current.id,
        mode: exportMode,
        format,
        ...(exportMode === 'redline' && compareTo ? { baseVersionId: compareTo.id } : {}),
      });
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setExporting('');
    }
  };

  // File-backed docs (received files, redline/comparison artifacts) can be
  // downloaded byte-for-byte — a re-export from the clause model would strip
  // tracked changes and original formatting.
  const exportOriginal = async () => {
    setExporting('original');
    try {
      const ext = (doc.s3Key.match(/\.\w+$/) || [''])[0];
      const safe = String(doc.title || 'document').replace(/[\\/:*?"<>|]+/g, '');
      await downloadAsset({ key: doc.s3Key, name: `${safe}${ext}` });
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setExporting('');
    }
  };

  // Delete = whole version tree, server side (rules keep clients from
  // removing sealed versions). Back to the project afterwards.
  const deleteThisDocument = async () => {
    const n = doc.versionCount || 1;
    if (!window.confirm(`Delete "${doc.title}" and its ${n} version${n === 1 ? '' : 's'} from this project? Proposals and history on it go too. The uploaded file stays recoverable by support.`)) return;
    setExporting('delete');
    try {
      await deleteDocument({ orgId: scope.org, dealId: scope.deal, docId: doc.id });
      toast(`Deleted "${doc.title}"`, 'ok');
      navigate(`/deals/${scope.deal}`);
    } catch (err) {
      toast(err.message, 'danger');
      setExporting('');
    }
  };

  // Graph impact per clause (only for the current official version — the
  // graph tracks currentVersionId; stale for historical views).
  const graphLive = graph && current && graph.versionId === current.id ? graph : null;
  const headingById = useMemo(
    () => Object.fromEntries((current?.clauses || []).map((c) => [c.id, c.heading])),
    [current]
  );

  // Deal points anchored per clause (side-rail annotations).
  const pointsByClause = useMemo(() => {
    const map = {};
    for (const p of dealPoints || []) {
      for (const r of p.clauseRefs || []) {
        if (r.docId !== docId) continue;
        (map[r.clauseId] = map[r.clauseId] || []).push(p);
      }
    }
    return map;
  }, [dealPoints, docId]);

  // "What changed since you last looked": per-user last-seen version lives in
  // localStorage (no schema change). The previous visit's value is captured
  // once per doc BEFORE this visit records itself; if the official draft has
  // moved past it, a dismissible orientation banner shows what landed.
  const [lastSeen, setLastSeen] = useState(null); // { versionId, number } | null
  const [seenBannerGone, setSeenBannerGone] = useState(false);
  const [bannerSummary, setBannerSummary] = useState(''); // '' | 'busy' | text
  useEffect(() => {
    setSeenBannerGone(false);
    setBannerSummary('');
    try { setLastSeen(JSON.parse(localStorage.getItem(`fl.lastSeen.${docId}`) || 'null')); } catch { setLastSeen(null); }
  }, [docId]);
  useEffect(() => {
    if (!doc?.currentVersionId) return;
    const off = (versions || []).find((v) => v.id === doc.currentVersionId);
    if (!off) return;
    try {
      localStorage.setItem(`fl.lastSeen.${docId}`, JSON.stringify({ versionId: doc.currentVersionId, number: off.number || 0, at: Date.now() }));
    } catch { /* private mode */ }
  }, [docId, doc?.currentVersionId, versions]);

  // NOTE: every hook must run on EVERY render — the loading / not-found /
  // workbook early returns live BELOW the last hook (React #310 otherwise:
  // a doc that loads async would add hooks on its second render and crash).
  // Everything between here and those returns is null-safe computation.
  const compareById = compareTo ? new Map(compareTo.clauses.map((c) => [c.id, c])) : null;
  const isDeck = docViewOf(doc) === 'deck';
  const official = sorted.find((v) => v.id === doc.currentVersionId) || sorted[sorted.length - 1];

  // Versions published after the reader's last visit (drives the banner).
  const sinceVersions = lastSeen && typeof lastSeen.number === 'number'
    ? sorted.filter((v) => v.number > lastSeen.number)
    : [];
  const showSeenBanner = !seenBannerGone
    && lastSeen
    && doc?.currentVersionId
    && lastSeen.versionId !== doc.currentVersionId
    && sinceVersions.length > 0;

  // "Changes only": in redline/side-by-side, hide the sections where nothing
  // moved — on a long agreement the movement IS the document.
  const diffing = viewMode !== 'clean' && !!compareById && !!current;
  const changedIds = diffing
    ? new Set(current.clauses.filter((c) => (compareById.get(c.id)?.text ?? '') !== c.text).map((c) => c.id))
    : new Set();

  // Removed sections (in the base, not in the viewed draft) render struck
  // through at their original position — a redline that hides deletions isn't
  // a redline. Each removed base clause slots in after the nearest preceding
  // base clause that survived (reverse order keeps consecutive removals in
  // their original sequence).
  const removedById = new Map();
  let displayAll = current ? current.clauses : [];
  if (diffing) {
    const curIds = new Set(current.clauses.map((c) => c.id));
    const removed = compareTo.clauses.filter((c) => !curIds.has(c.id));
    if (removed.length) {
      for (const r of removed) removedById.set(r.id, r);
      const baseIds = compareTo.clauses.map((c) => c.id);
      displayAll = [...current.clauses];
      for (const r of [...removed].reverse()) {
        const i = baseIds.indexOf(r.id);
        let anchorPos = -1;
        for (let j = i - 1; j >= 0; j -= 1) {
          const pos = displayAll.findIndex((c) => c.id === baseIds[j]);
          if (pos >= 0) { anchorPos = pos; break; }
        }
        displayAll.splice(anchorPos + 1, 0, r);
      }
    }
  }
  const changedCount = changedIds.size + removedById.size;
  const visibleClauses = diffing && changesOnly
    ? displayAll.filter((c) => changedIds.has(c.id) || removedById.has(c.id))
    : displayAll;

  // Progressive rendering over whatever survives the filters above: the sheet
  // holds chunks 1..renderedPages and grows as the reader scrolls. Slide
  // decks stay unchunked (each slide already reads as a card).
  const docPageCount = isDeck ? 1 : Math.max(1, Math.ceil(visibleClauses.length / DOC_PAGE_SIZE));
  // Paper mode renders the Word print layout (WordLayout.js): measured
  // US-Letter pages mirroring the .docx export. Decks and side-by-side keep
  // the card layout — neither maps to a single Word page flow.
  const wordLayout = paper && !isDeck && viewMode !== 'sxs';
  const shownPages = Math.min(renderedPages, docPageCount);
  const pageClauses = docPageCount > 1
    ? visibleClauses.slice(0, shownPages * DOC_PAGE_SIZE)
    : visibleClauses;
  // Jump nav (thumbnails / jump field): mount every chunk up to the target,
  // then scroll to its anchor once it exists.
  const gotoPage = (p) => {
    const target = Math.min(docPageCount, Math.max(1, Math.round(p) || 1));
    setRenderedPages((n) => Math.max(n, target));
    setPendingChunk(target);
  };
  useEffect(() => {
    if (!pendingChunk || shownPages < pendingChunk) return;
    const el = document.getElementById(`doc-chunk-${pendingChunk}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setPendingChunk(0);
    }
  }, [pendingChunk, shownPages]);

  // Auto-append: a sentinel below the sheet mounts the next chunk when the
  // reader scrolls within ~1.5 viewports of the end — the document just
  // keeps going. The observer re-arms after every append, so a fast scroll
  // (or a tall viewport) cascades until the sentinel falls out of range.
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setRenderedPages((n) => Math.min(docPageCount, n + 1));
      }
    }, { rootMargin: '1400px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [docPageCount, shownPages]);

  // Live reading position for the pager label + thumbnails: the highest
  // chunk anchor above the reading line (30% down the viewport).
  useEffect(() => {
    if (docPageCount <= 1) { setScrollChunk(1); return undefined; }
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        let cur = 1;
        const line = window.innerHeight * 0.3;
        document.querySelectorAll('[id^="doc-chunk-"]').forEach((el) => {
          if (el.getBoundingClientRect().top <= line) {
            cur = Math.max(cur, Number(el.id.replace('doc-chunk-', '')) || 1);
          }
        });
        setScrollChunk(cur);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [docPageCount, shownPages, viewMode, changesOnly, current?.id]);

  // ---- Early returns (all hooks are above this line) ----
  if (!orgId || doc === undefined || !deal) {
    return <div className="cs-page"><div className="cs-skeleton" style={{ height: 200 }} /></div>;
  }
  if (doc === null) {
    return (
      <div className="cs-page">
        <div className="cs-banner b-danger">Document not found.</div>
      </div>
    );
  }

  // Workbooks (.xlsx) render on their own surface: sheet grid + model
  // intelligence + cell-level change manifests + the workbook agent bar.
  // The clause canon still exists underneath (sheet projections) for the
  // brain/graph — but the grid is the right way to READ a spreadsheet.
  if (docViewOf(doc) === 'workbook') {
    return (
      <WorkbookView
        scope={scope}
        deal={deal}
        doc={doc}
        versions={versions || []}
        can={can}
      />
    );
  }

  // Attribute every changed clause to the version(s) that touched it between
  // the base and the viewed draft — walking each intermediate turn. Powers
  // the hover "who changed this, and when" on redline ins/del marks, and the
  // per-editor party color on each clause.
  const fmtWhen = (t) => new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const asEditor = (v) => ({
    number: v.number,
    who: v.authorName || 'unknown',
    party: v.partyKey ? partyLabel(v.partyKey) : '',
    partyKey: v.partyKey || '',
    when: fmtWhen(v.createdAt),
  });
  const editsByClause = {};
  if (diffing) {
    let prev = compareTo;
    for (const v of sorted.filter((x) => x.number > compareTo.number && x.number <= current.number)) {
      const prevText = new Map(prev.clauses.map((c) => [c.id, c.text]));
      for (const c of v.clauses) {
        if ((prevText.get(c.id) ?? '') !== c.text) {
          (editsByClause[c.id] = editsByClause[c.id] || []).push(asEditor(v));
        }
      }
      prev = v;
    }
    // Comparing against a non-ancestor (or a later version) still shows
    // movement — attribute it to the viewed draft itself.
    for (const id of changedIds) {
      if (!editsByClause[id]) editsByClause[id] = [asEditor(current)];
    }
  }

  // Explanation rail data: for every change between the base and the viewed
  // draft, the recorded reasoning from each version's manifest — the derive
  // plan's per-section rationale, edit instructions/summaries, reconciliation
  // notes. Versions are walked along the baseVersionId ANCESTRY (not by
  // number), so an orphaned concurrent branch never pollutes the story; if
  // the base isn't an ancestor, fall back to the number range.
  const explainByClause = {};
  const explainCards = [];
  if (diffing) {
    const vById = new Map(sorted.map((v) => [v.id, v]));
    let chain = [];
    let node = current;
    const seen = new Set();
    while (node && node.id !== compareTo.id && !seen.has(node.id)) {
      seen.add(node.id);
      chain.unshift(node);
      node = node.baseVersionId ? vById.get(node.baseVersionId) : null;
    }
    if (!node) chain = sorted.filter((x) => x.number > compareTo.number && x.number <= current.number);
    for (const v of chain) {
      const m = v.manifest;
      if (!m?.ops) continue;
      const via = m.source === 'derive' ? 'AI derive'
        : m.via === 'reconcile' ? 'reconciled'
          : m.via === 'new-draft' || m.source === 'upload' ? 'received draft'
            : m.mode === 'vibe' ? 'AI edit' : 'proposal applied';
      for (const op of m.ops) {
        const why = String(op.rationale || v.label || m.instruction || '').slice(0, 300);
        const list = (explainByClause[op.id] = explainByClause[op.id] || []);
        if (!list.some((n) => n.why === why)) list.push({ v: v.number, when: v.createdAt, via, op: op.op, why });
      }
    }
    for (const c of displayAll) {
      if (!changedIds.has(c.id) && !removedById.has(c.id)) continue;
      explainCards.push({
        id: c.id,
        heading: c.heading,
        removed: removedById.has(c.id),
        added: !!compareById && !compareById.get(c.id),
        notes: explainByClause[c.id] || [],
        editors: editsByClause[c.id] || [],
      });
    }
  }
  const jumpToClause = (id) => {
    const idx = visibleClauses.findIndex((c) => c.id === id);
    if (idx >= 0 && docPageCount > 1) {
      const needed = Math.floor(idx / DOC_PAGE_SIZE) + 1;
      if (needed > renderedPages) setRenderedPages(needed);
    }
    setFocusClauseId(id);
    requestAnimationFrame(() => document.getElementById(`clause-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  // Lineage across documents (versions within THIS doc live in the rail):
  // the template it was derived from, the draft(s) it replaced, and — when
  // this doc is itself history — the current draft that replaced it.
  const byId = Object.fromEntries((siblingDocs || []).map((d) => [d.id, d]));
  const supersededHere = !!(doc.supersededBy || (doc.tags || []).includes('superseded'));
  const successor = doc.supersededBy ? byId[doc.supersededBy] : null;
  const predecessors = (siblingDocs || []).filter((d) => d.supersededBy === docId);
  const derivedFrom = doc.deriveSpec?.sourceDocId ? byId[doc.deriveSpec.sourceDocId] : null;

  // Soft edit lease (backend-held while a version-producing AI task runs):
  // surface it so a human doesn't fire a competing edit that will be
  // rejected. Expired lease = crashed task — ignore it.
  const lease = doc.editLease && (doc.editLease.expiresAt || 0) > Date.now() ? doc.editLease : null;

  return (
    <div className="cs-page u-stack m-rise" style={{ '--gap': 'var(--sp-5)' }}>
      <SpotlightTour
        tourId="docview"
        steps={[
          { target: '[data-tour="view-controls"]', title: '📄 Three ways to read', body: 'Clean is the text as it would be signed. Redline marks every change against a base version you pick, with reasons. Side-by-side shows both drafts in parallel. The version rail above jumps to any point in the document\u2019s history.' },
          { target: '[data-tour="agent-panel"]', title: '✍️ Edit in plain language', body: 'Tell the drafting agent what to change. Precision mode proposes a reviewable change list — approve all, some, or send it back with notes. Every change is recorded in the version\u2019s manifest and revertible.' },
        ]}
      />
      <div className="u-small u-muted">
        <Link to="/projects">Projects</Link> / <Link to={`/deals/${dealId}`}>{deal.name}</Link> / {doc.title}
      </div>

      {lease && (
        <div className="cs-banner b-info u-small">
          🔒 An AI task is editing this document right now ({lease.kind === 'draft-ingest' ? 'ingesting a received draft' : 'agent edit'}
          {lease.label ? ` — "${lease.label}"` : ''}). New edits and publishes are held off until it finishes, so nothing overwrites its work.
        </div>
      )}

      {supersededHere && (
        <div className="cs-banner b-warn u-small">
          🗂 This draft has been superseded — it is preserved for lineage, with every version intact.
          {successor && (
            <>
              {' '}Current draft: <Link to={`/deals/${dealId}/docs/${successor.id}`}><strong>{successor.title}</strong></Link>
            </>
          )}
        </div>
      )}

      {/* Orientation: what landed since this reader's last visit. */}
      {showSeenBanner && (
        <div className="cs-banner b-info u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <div className="u-split" style={{ alignItems: 'flex-start' }}>
            <span className="u-small">
              <strong>
                {sinceVersions.length === 1
                  ? `v${sinceVersions[0].number} was published`
                  : `${sinceVersions.length} versions were published`} since you last viewed this document
              </strong>
              {' — '}
              {sinceVersions.slice(-3).map((v) =>
                `v${v.number}${v.label ? ` (${v.label})` : ''} · ${v.authorName || 'unknown'} · ${new Date(v.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`).join('; ')}
              {sinceVersions.length > 3 ? '; …' : ''}
            </span>
            <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0 }}>
              {isAiConfigured() && (
                <button
                  type="button"
                  className="cs-btn cs-btn-outline cs-btn-sm"
                  disabled={bannerSummary === 'busy'}
                  onClick={async () => {
                    setBannerSummary('busy');
                    try {
                      const r = await aiSummarize({ orgId, dealId, docId, partyKey: participant?.partyKey || '' });
                      setBannerSummary(r.summary || 'No summary available.');
                    } catch (err) {
                      setBannerSummary('');
                      toast(err.message, 'danger');
                    }
                  }}
                >
                  {bannerSummary === 'busy' ? 'Summarizing…' : 'Summary'}
                </button>
              )}
              <button
                type="button"
                className="cs-btn cs-btn-outline cs-btn-sm"
                onClick={() => { setViewVersionId(doc.currentVersionId); setShowChanges(true); }}
              >
                Open changes
              </button>
              <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setSeenBannerGone(true)}>
                Dismiss
              </button>
            </span>
          </div>
          {bannerSummary && bannerSummary !== 'busy' && (
            <div className="u-small" style={{ whiteSpace: 'pre-wrap' }}>{bannerSummary}</div>
          )}
        </div>
      )}

      <div className="cs-page-head">
        <div>
          <h1>{doc.title}</h1>
          <p className="u-muted u-small">
            {cabinetOf(doc) === 'deliverable' && <span className="cs-badge b-ok" style={{ marginRight: 'var(--sp-2)' }} data-tip="Filed in the Deliverables cabinet — download the pinned file below, not an export">📤 deliverable</span>}
            {cabinetOf(doc) === 'input' && <span className="cs-badge b-info" style={{ marginRight: 'var(--sp-2)' }} data-tip="Filed in the Inputs cabinet — a source this project works from">📥 input</span>}
            {doc.kind} · <DocTypeBadge scope={scope} doc={doc} canEdit={can('edit')} /> · {doc.format || '—'} ·{' '}
            {doc.status === 'ready'
              ? <>{sorted.length} <Term term="lineage">version{sorted.length === 1 ? '' : 's'}</Term></>
              : doc.status}
          </p>
          {(derivedFrom || predecessors.length > 0) && (
            <p className="u-xs u-muted" style={{ marginTop: 2 }}>
              {derivedFrom && (
                <>🧬 derived from <Link to={`/deals/${dealId}/docs/${derivedFrom.id}`}>{derivedFrom.title}</Link></>
              )}
              {derivedFrom && predecessors.length > 0 && ' · '}
              {predecessors.length > 0 && (
                <>
                  ↩ supersedes{' '}
                  {predecessors.map((p, i) => (
                    <span key={p.id}>
                      {i > 0 && ', '}
                      <Link to={`/deals/${dealId}/docs/${p.id}`}>{p.title}</Link>
                    </span>
                  ))}
                </>
              )}
            </p>
          )}
        </div>
        {!isDeck && doc.status === 'ready' && can('upload') && (
          <button
            className="cs-btn cs-btn-primary"
            data-tip="Received a new turn from the other side? Ingest it as the next version — redline, deal points, and analysis included"
            onClick={() => {
              console.log('[fast.legal] Upload new draft button clicked', { docId: doc.id, status: doc.status });
              setShowNewDraft(true);
            }}
          >
            Upload new draft
          </button>
        )}
      </div>

      {deal && (
        <CommandPanel
          embedded
          orgId={orgId}
          dealId={dealId}
          dealName={deal.name}
          deal={deal}
          participant={participant}
          defaultDocId={docId}
        />
      )}

      {doc.status === 'queued' || doc.status === 'processing' ? (
        doc.progress ? (
          <LiveTaskFeed scope={scope} progress={doc.progress} />
        ) : (
          <div className="cs-banner b-info" role="status">
            {doc.newDraft
              ? `Ingesting the received draft as v${(doc.versionCount || 1) + 1} — matching sections to the current draft and analyzing what changed…`
              : doc.deriveSpec
                ? 'Queued — the refactoring agent will pick this up in a moment…'
                : 'Importing — extracting text, splitting clauses, and running AI smart import…'}
          </div>
        )
      ) : null}
      {doc.status === 'error' && (
        <div className="cs-banner b-danger">Import failed: {doc.error || 'unknown error'}</div>
      )}
      {/* Ambiguities the refactoring agent refused to guess on — it kept the
          source language there; answer via the drafting agent or an edit. */}
      {doc.status === 'ready' && (doc.agentQuestions || []).length > 0 && (
        <div className="cs-banner b-warn u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <span>
            <strong>The agent has {doc.agentQuestions.length === 1 ? 'a question' : 'questions'} it could not
            resolve from your brief</strong> — the source language was kept in those spots rather than guessed:
          </span>
          <ul style={{ margin: 0, paddingLeft: 'var(--sp-5)' }}>
            {doc.agentQuestions.map((q) => {
              const text = typeof q === 'string' ? q : q.question;
              return <li key={text} className="u-small">{text}</li>;
            })}
          </ul>
          <span className="u-small u-muted">
            Answer these on the project dashboard (the agent applies your answer as a tracked edit), with the
            drafting agent below, or edit the clauses directly — then dismiss.
          </span>
          <span className="u-cluster">
            <button
              type="button" className="cs-btn cs-btn-outline cs-btn-sm"
              onClick={() => updateEntity('dealDoc', scope, doc.id, { agentQuestions: [] })}
            >
              Dismiss
            </button>
          </span>
        </div>
      )}
      {/* Shape guard verdict: the upload didn't resemble this document, so it
          was NOT published — offer to file it as its own document instead. */}
      {doc.newDraftRejected && (
        <div className="cs-banner b-warn u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <span><strong>Upload not published.</strong> {doc.newDraftRejected.reason}</span>
          <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
            {(doc.newDraftRejected.files || []).some((f) => f.role === 'source') && (
              <button
                className="cs-btn cs-btn-outline cs-btn-sm"
                onClick={async () => {
                  try {
                    const rej = doc.newDraftRejected;
                    const src = rej.files.find((f) => f.role === 'source');
                    const newId = await createEntity('dealDoc', scope, {
                      title: rej.label || src.name.replace(/\.[^.]+$/, ''),
                      kind: 'other',
                      docType: 'general',
                      format: (src.name.match(/\.([a-z0-9]+)$/i)?.[1] || 'docx').toLowerCase(),
                      s3Key: src.s3Key,
                      status: 'queued',
                      createdBy: user.uid,
                    });
                    await updateEntity('dealDoc', scope, docId, { newDraftRejected: null });
                    toast('Filed as its own document — importing now', 'ok');
                    navigate(`/deals/${dealId}/docs/${newId}`);
                  } catch (err) {
                    toast(err.message, 'danger');
                  }
                }}
              >
                File it as its own document
              </button>
            )}
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={() => updateEntity('dealDoc', scope, docId, { newDraftRejected: null })}
            >
              Dismiss
            </button>
          </span>
        </div>
      )}

      {(doc.status === 'ready' || doc.newDraft) && current && (
        <>
          <AiPanel orgId={orgId} dealId={dealId} docId={docId} doc={doc} versions={sorted} participant={participant} />
          <AgentPanel scope={scope} doc={doc} current={current} dealPoints={dealPoints} participant={participant} />

          {/* View controls: mode, version pickers with party filter, integrity, Word export */}
          <div className="cs-card u-stack" data-tour="view-controls" style={{ '--gap': 'var(--sp-3)' }}>
            {/* Where am I on the timeline? Click a turn to read that draft. */}
            {sorted.length > 1 && (
              <VersionRail
                doc={doc}
                versions={sorted}
                partyColors={partyColors}
                viewingId={current.id}
                compareToId={diffing ? compareTo?.id : null}
                onSelect={(v) => setViewVersionId(v.id)}
              />
            )}
            <div className="u-cluster" style={{ '--gap': 'var(--sp-3)', alignItems: 'center' }}>
              <div className="cs-viewmode" role="group" aria-label="View mode">
                {[['clean', 'Clean'], ['redline', 'Redline'], ['sxs', 'Side by side']].map(([m, label]) => (
                  <button key={m} type="button" className={viewMode === m ? 'active' : ''} onClick={() => setViewMode(m)}>
                    {label}
                  </button>
                ))}
              </div>
              <HelpDot topic="doc-views" label="Clean vs redline vs side-by-side — what each view shows" />
              {doc.format === 'derived' && sorted.length > 1 && (
                <button
                  type="button"
                  className="cs-btn cs-btn-outline cs-btn-sm"
                  onClick={() => {
                    setViewVersionId(null); // latest official draft
                    setCompareToId(sorted[0].id); // v1 = the source template snapshot
                    setViewMode('redline');
                    setShowExplain(true);
                    setRenderedPages(1);
                    setScrollChunk(1);
                  }}
                  data-tip="Full redline of the current draft against the source template it was derived from — with the reasoning beside every change"
                >
                  🧬 vs source template
                </button>
              )}
              <button
                type="button"
                className={`cs-btn cs-btn-sm ${paper ? 'cs-btn-primary' : 'cs-btn-outline'}`}
                onClick={() => setPaper((p) => !p)}
                aria-pressed={paper}
                data-tip="Word-like page rendering"
              >
                Paper view
              </button>
              <button
                type="button"
                className={`cs-btn cs-btn-sm ${showOutline ? 'cs-btn-primary' : 'cs-btn-outline'}`}
                onClick={() => setShowOutline((s) => !s)}
                aria-pressed={showOutline}
                data-tip="Table of contents — click a section to jump"
              >
                Outline
              </button>
              {(graph?.facts || []).length > 0 && (
                <button
                  type="button"
                  className={`cs-btn cs-btn-sm ${showFacts ? 'cs-btn-primary' : 'cs-btn-outline'}`}
                  onClick={() => setShowFacts((s) => !s)}
                  aria-pressed={showFacts}
                  data-tip="Entities, dates, amounts, addresses, jurisdictions — parsed from the draft, each with a tracked replace-everywhere"
                >
                  Key facts · {graph.facts.length}
                </button>
              )}
              {diffing && (
                <button
                  type="button"
                  className={`cs-btn cs-btn-sm ${changesOnly ? 'cs-btn-primary' : 'cs-btn-outline'}`}
                  onClick={() => setChangesOnly((s) => !s)}
                  aria-pressed={changesOnly}
                  data-tip="Hide unchanged sections — see only where the language moved"
                >
                  Changes only · {changedCount}
                </button>
              )}
              {diffing && explainCards.length > 0 && (
                <button
                  type="button"
                  className={`cs-btn cs-btn-sm ${showExplain ? 'cs-btn-primary' : 'cs-btn-outline'}`}
                  onClick={() => setShowExplain((s) => !s)}
                  aria-pressed={showExplain}
                  data-tip="Side panel with the reasoning behind each change, aligned with the redline in document order"
                >
                  🧠 Why · {explainCards.length}
                </button>
              )}
              <span style={{ flex: 1 }} />
              {can('download') && doc?.s3Key && (
                <button
                  className="cs-btn cs-btn-outline cs-btn-sm"
                  disabled={!!exporting}
                  data-tip="The file exactly as filed — original formatting and any tracked changes intact"
                  onClick={exportOriginal}
                >
                  {exporting === 'original' ? 'Downloading…' : '⬇ Original file'}
                </button>
              )}
              {!can('download') ? null : isDeck ? (
                <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!exporting} onClick={() => exportWord('clean')}>
                  {exporting === 'clean-docx' ? 'Exporting…' : 'Download PowerPoint'}
                </button>
              ) : (
                <>
                  <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!exporting} onClick={() => exportWord('clean')}>
                    {exporting === 'clean-docx' ? 'Exporting…' : 'Word (clean)'}
                  </button>
                  <button
                    className="cs-btn cs-btn-outline cs-btn-sm"
                    disabled={!!exporting || !compareTo}
                    data-tip={compareTo ? `Word tracked changes vs v${compareTo.number}` : 'Needs a base version'}
                    onClick={() => exportWord('redline')}
                  >
                    {exporting === 'redline-docx' ? 'Exporting…' : 'Word (redline)'}
                  </button>
                  <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!exporting} onClick={() => exportWord('clean', 'pdf')}>
                    {exporting === 'clean-pdf' ? 'Exporting…' : 'PDF (clean)'}
                  </button>
                  <button
                    className="cs-btn cs-btn-outline cs-btn-sm"
                    disabled={!!exporting || !compareTo}
                    data-tip={compareTo ? `Redline vs v${compareTo.number} with the reasoning for each change in the right margin` : 'Needs a base version'}
                    onClick={() => exportWord('redline', 'pdf')}
                  >
                    {exporting === 'redline-pdf' ? 'Exporting…' : 'PDF (redline + reasons)'}
                  </button>
                </>
              )}
              {can('manage') && (
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  style={{ color: 'var(--danger)' }}
                  disabled={!!exporting}
                  data-tip="Removes this document and all its versions from the project (the uploaded file stays recoverable by support)"
                  onClick={deleteThisDocument}
                >
                  {exporting === 'delete' ? 'Deleting…' : 'Delete document'}
                </button>
              )}
            </div>

            <div className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
              {parties.length > 0 && (
                <>
                  <label className="u-small u-muted" htmlFor="v-party">Side</label>
                  <select
                    id="v-party"
                    className="cs-input cs-select-inline"
                    value={partyFilter}
                    onChange={(e) => setPartyFilter(e.target.value)}
                    aria-label="Filter versions by party"
                  >
                    <option value="">All parties</option>
                    {parties.map((p) => <option key={p.key} value={p.key}>{p.label} versions</option>)}
                  </select>
                </>
              )}
              <label className="u-small u-muted" htmlFor="v-view">Viewing</label>
              <select
                id="v-view"
                className="cs-input cs-select-inline"
                value={current.id}
                onChange={(e) => setViewVersionId(e.target.value)}
              >
                {(versionOptions.some((v) => v.id === current.id) ? versionOptions : [current, ...versionOptions]).map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.number} — {v.label || 'untitled'} ({v.partyKey ? partyLabel(v.partyKey) : v.authorName}) · {new Date(v.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </option>
                ))}
              </select>
              {viewMode !== 'clean' && (
                <>
                  <label className="u-small u-muted" htmlFor="v-compare">against</label>
                  <select
                    id="v-compare"
                    className="cs-input cs-select-inline"
                    value={compareTo?.id || ''}
                    onChange={(e) => setCompareToId(e.target.value || null)}
                  >
                    {sorted.filter((v) => v.id !== current.id).map((v) => (
                      <option key={v.id} value={v.id}>
                        v{v.number} — {v.label || 'untitled'} ({v.partyKey ? partyLabel(v.partyKey) : v.authorName}) · {new Date(v.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </option>
                    ))}
                  </select>
                </>
              )}
              {current.id === doc.currentVersionId && <span className="cs-badge b-ok">official current draft</span>}
              {current.sealed && current.checkpoint ? (
                <span
                  className="cs-chip u-tabular"
                  data-tip={`Sealed ${new Date(current.checkpoint.sealedAt).toLocaleString()} — chained SHA-256 checkpoint, archived to S3`}
                >
                  sealed · {current.checkpoint.chainHash.slice(0, 12)}…
                </span>
              ) : (
                <span className="cs-chip u-muted" data-tip="The checkpoint worker seals every version within ~1 min">
                  seal pending
                </span>
              )}
              <button
                className="cs-btn cs-btn-outline cs-btn-sm"
                data-tip="What produced this version: every change with before/after, traceable and individually revertible"
                onClick={() => setShowChanges(true)}
              >
                Changes{current.manifest?.ops ? ` · ${current.manifest.ops.length}` : ''}
              </button>
              <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setShowVerify(true)}>
                Verify integrity
              </button>
              <span
                className={`cs-chip u-xs u-tabular ${sorted.every((v) => v.sealed && v.checkpoint) ? '' : 'u-muted'}`}
                style={sorted.length && sorted.every((v) => v.sealed && v.checkpoint) ? { color: 'var(--ok)', borderColor: 'var(--ok)' } : undefined}
                data-tip="How many versions are sealed — see the checkpoint chain below"
              >
                ⛓ {sorted.filter((v) => v.sealed && v.checkpoint).length}/{sorted.length} sealed
              </span>
              {!isDeck && (
                <>
                  <Link className="cs-btn cs-btn-outline cs-btn-sm" to={`/deals/${dealId}/docs/${docId}/compliance`}>
                    Jurisdiction check
                  </Link>
                  {isAiConfigured() && (
                    <button
                      className="cs-btn cs-btn-outline cs-btn-sm"
                      data-tip="AI distills this draft into a new slide deck (source-traced in its manifest)"
                      onClick={() => setShowConvert(true)}
                    >
                      Convert to deck
                    </button>
                  )}
                  {isAiConfigured() && sorted.length > 1 && (
                    <button
                      className="cs-btn cs-btn-outline cs-btn-sm"
                      disabled={memoBusy}
                      data-tip="File a changes memo narrating this document's recorded edits, version by version — lands as a project document"
                      onClick={async () => {
                        setMemoBusy(true);
                        try {
                          const r = await aiGenerateMemo({ orgId, dealId, docId, kind: 'changes' });
                          toast(`Filed "${r.title}"`, 'ok');
                          navigate(`/deals/${dealId}/docs/${r.docId}`);
                        } catch (err) {
                          toast(err.message, 'danger');
                        } finally {
                          setMemoBusy(false);
                        }
                      }}
                    >
                      {memoBusy ? 'Filing memo…' : 'Changes memo'}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Immutability demarcation — the checkpoint chain at a glance.
                Each sealed version shows its chain hash; every link references
                its predecessor, so tampering after sealing breaks every later
                link. Click a link to view that version; Verify integrity
                recomputes the whole chain against the S3 archives. */}
            {sorted.length > 0 && (
              <div className="u-cluster u-xs" style={{ '--gap': 'var(--sp-1)', alignItems: 'center', rowGap: 'var(--sp-2)' }}>
                <span
                  className="u-muted"
                  style={{ marginRight: 'var(--sp-1)' }}
                  data-tip="Chained SHA-256 checkpoints: each version's hash covers its full text and references the previous seal, and every seal is archived immutably to S3"
                >
                  Checkpoint chain:
                </span>
                {sorted.map((v, i) => {
                  const isSealed = !!(v.sealed && v.checkpoint);
                  return (
                    <React.Fragment key={v.id}>
                      {i > 0 && <span className="u-muted" aria-hidden="true">─</span>}
                      <button
                        type="button"
                        className="cs-chip u-xs u-tabular"
                        style={{
                          cursor: 'pointer',
                          ...(isSealed ? { color: 'var(--ok)', borderColor: 'var(--ok)' } : { opacity: 0.65 }),
                          ...(v.id === current.id ? { fontWeight: 700 } : {}),
                        }}
                        data-tip={isSealed
                          ? `v${v.number} sealed ${new Date(v.checkpoint.sealedAt).toLocaleString()} · chain ${v.checkpoint.chainHash.slice(0, 16)}… · click to view`
                          : `v${v.number} — seal pending (the checkpoint worker seals within ~1 min) · click to view`}
                        onClick={() => setViewVersionId(v.id)}
                      >
                        {isSealed ? '⛓' : '◌'} v{v.number}·{isSealed ? v.checkpoint.chainHash.slice(0, 8) : 'pending'}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {(current.files || []).length > 0 && (
              <div className="u-cluster u-xs" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
                <span className="u-muted">Files at this checkpoint:</span>
                {current.files.map((f) => (
                  <button
                    key={f.s3Key}
                    type="button"
                    className="cs-chip"
                    data-tip={`${f.role === 'source' ? 'Ingested draft source' : 'Reference copy — kept, not ingested'}${f.sha256 ? ` · SHA-256 ${f.sha256.slice(0, 16)}…` : ''} — click to download`}
                    onClick={() =>
                      downloadAsset({ key: f.s3Key, name: f.name }).catch((err) => toast(err.message, 'danger'))
                    }
                  >
                    {f.name}
                    <span className="u-muted"> · {f.role === 'source' ? 'source' : 'reference'}</span>
                  </button>
                ))}
              </div>
            )}

            {viewMode !== 'clean' && parties.length > 0 && (
              <div className="u-cluster u-xs u-muted" style={{ '--gap': 'var(--sp-3)' }}>
                <span>Edit colors:</span>
                {parties.map((p) => (
                  <span key={p.key} className="u-cluster" style={{ '--gap': '4px', alignItems: 'center' }}>
                    <span className="cs-sidebar-dot" style={{ background: partyColors[p.key] }} />
                    {p.label}
                  </span>
                ))}
                <span>— insertions underlined in the editing party's color, deletions struck through</span>
              </div>
            )}
          </div>

          {/* AI change brief — written by the worker when this version was a received draft */}
          {current.analysis && (current.analysis.summary || (current.analysis.affectedDealPoints || []).length > 0) && (
            <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)', borderLeft: '3px solid var(--info)' }}>
              <div className="u-split">
                <strong className="u-small">AI review of this received draft (v{current.number})</strong>
                <Link className="u-xs" to={`/deals/${dealId}`}>Open the deal points register →</Link>
              </div>
              {current.analysis.summary && (
                <p className="u-small" style={{ margin: 0, lineHeight: 1.6 }}>{current.analysis.summary}</p>
              )}
              {(current.analysis.riskNotes || []).length > 0 && (
                <ul className="u-small" style={{ margin: 0, paddingLeft: '1.2em', lineHeight: 1.6 }}>
                  {current.analysis.riskNotes.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
              {(current.analysis.keyAsks || []).length > 0 && (
                <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
                  <span className="u-xs u-muted" style={{ letterSpacing: '0.06em' }}>
                    KEY ASKS FROM THE CORRESPONDENCE
                  </span>
                  {current.analysis.keyAsks.map((k, i) => (
                    <span key={i} className="u-small">
                      <strong>{k.from}</strong>: {k.ask}
                      {k.clauseId && headingById[k.clauseId] && (
                        <>
                          {' '}
                          <a className="u-xs" href={`#clause-${k.clauseId}`}>({headingById[k.clauseId]} →)</a>
                        </>
                      )}
                    </span>
                  ))}
                </div>
              )}
              {(current.analysis.affectedDealPoints || []).length > 0 && (
                <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
                  <span className="u-xs u-muted" style={{ letterSpacing: '0.06em' }}>
                    DEAL POINTS TOUCHED — flagged “needs review” in the register
                  </span>
                  {current.analysis.affectedDealPoints.map((p) => (
                    <span key={p.id} className="u-small">
                      <strong>{p.title}</strong>{p.why ? <span className="u-muted"> — {p.why}</span> : null}
                    </span>
                  ))}
                </div>
              )}
              {current.context && (
                <details className="u-small">
                  <summary className="u-muted" style={{ cursor: 'pointer' }}>
                    View the correspondence that arrived with this draft
                  </summary>
                  <div style={{ whiteSpace: 'pre-wrap', marginTop: 'var(--sp-2)', maxHeight: 260, overflow: 'auto', borderLeft: '2px solid var(--border)', paddingLeft: 'var(--sp-3)' }}>
                    {current.context}
                  </div>
                </details>
              )}
            </div>
          )}

          {showOutline && <OutlinePanel clauses={current.clauses} targetId={targetClauseId} />}
          {showFacts && (
            <FactsPanel
              facts={graph?.facts || []}
              headingById={headingById}
              canReplace={doc.status === 'ready' && !!official}
              onReplace={(fact, initialNext, reviewKey) => setReplaceFact({ fact, initialNext: initialNext || '', reviewKey: reviewKey || '' })}
              scope={scope}
              doc={doc}
              participant={participant}
            />
          )}

          {/* The chunk pager only makes sense for the card layout — the Word
              layout has real page frames and footers for position. */}
          {!wordLayout && (
            <>
              <DocPager page={scrollChunk} pageCount={docPageCount} loaded={shownPages} onPage={gotoPage} showThumbs={showThumbs} onToggleThumbs={() => setShowThumbs((s) => !s)} />
              {showThumbs && docPageCount > 1 && (
                <PageThumbs clauses={visibleClauses} page={scrollChunk} onPage={gotoPage} />
              )}
            </>
          )}

          <div style={{ display: 'flex', gap: 'var(--sp-5)', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {wordLayout ? (
                <>
                  {diffing && changesOnly && !visibleClauses.length ? (
                    <div className="cs-empty">No differences between v{compareTo.number} and v{current.number}.</div>
                  ) : (
                    <WordPages
                      title={doc.title}
                      clauses={pageClauses}
                      mode={diffing ? 'redline' : 'clean'}
                      compareById={compareById}
                      removedById={removedById}
                      versionKey={`${current.id}|${compareTo ? compareTo.id : ''}`}
                      structured={!diffing && (docViewOf(doc) === 'design' || doc.format === 'authored')}
                      colorFor={(id) => {
                        const eds = editsByClause[id];
                        const le = eds ? eds[eds.length - 1] : null;
                        return (le && partyColors[le.partyKey]) || currentPartyColor;
                      }}
                      tipFor={(id) => {
                        const eds = editsByClause[id];
                        const le = eds ? eds[eds.length - 1] : null;
                        return le ? `${le.who}${le.party ? ` (${le.party})` : ''} · v${le.number} · ${le.when}` : `v${current.number}`;
                      }}
                      totalKnown={shownPages >= docPageCount}
                      targetId={focusClauseId || targetClauseId}
                      chunkSize={DOC_PAGE_SIZE}
                      onEditClause={doc.status === 'ready' ? (c) => setEditingClause(c) : null}
                    />
                  )}
                  {shownPages < docPageCount && (
                    <div ref={loadMoreRef} aria-hidden="true" style={{ padding: 'var(--sp-3) 0' }}>
                      <div className="cs-skeleton" style={{ height: 48, maxWidth: 816, margin: '0 auto' }} />
                    </div>
                  )}
                </>
              ) : (
              <div
                className={`${paper ? 'cs-paper' : ''}${viewMode === 'sxs' ? ' cs-paper-wide' : ''} u-stack ${!paper ? 'cs-clause-list' : ''}`}
                style={{ '--gap': paper ? 'var(--sp-3)' : 'var(--sp-4)' }}
              >
                {paper && <div className="cs-paper-title">{doc.title}</div>}
                {viewMode === 'sxs' && compareTo && (
                  <div className="cs-sxs cs-sxs-head" aria-hidden="true">
                    <span>v{compareTo.number} — {compareTo.label || compareTo.authorName} · CLEAN</span>
                    <span style={{ color: currentPartyColor }}>v{current.number} — {current.label || current.authorName} · REDLINE</span>
                  </div>
                )}
                {diffing && changesOnly && !visibleClauses.length && (
                  <div className="cs-empty">No differences between v{compareTo.number} and v{current.number}.</div>
                )}
                {pageClauses.map((clause, ci) => {
                  const editors = editsByClause[clause.id];
                  const lastEditor = editors ? editors[editors.length - 1] : null;
                  const isRemoved = removedById.has(clause.id);
                  return (
                    <React.Fragment key={clause.id}>
                    {docPageCount > 1 && ci % DOC_PAGE_SIZE === 0 && (
                      // Invisible chunk anchor — jump target for the pager /
                      // thumbnails and the reading-position tracker's marker.
                      <span id={`doc-chunk-${ci / DOC_PAGE_SIZE + 1}`} aria-hidden="true" style={{ display: 'block', height: 0, scrollMarginTop: 76 }} />
                    )}
                    <ClauseCard
                      scope={scope}
                      docId={docId}
                      deck={isDeck}
                      slideNo={current.clauses.indexOf(clause) + 1}
                      clause={clause}
                      removed={isRemoved}
                      anchored={pointsByClause[clause.id] || []}
                      base={compareById ? compareById.get(clause.id) : null}
                      mode={viewMode !== 'clean' && compareTo ? viewMode : 'clean'}
                      partyColor={(lastEditor && partyColors[lastEditor.partyKey]) || currentPartyColor}
                      editors={editors}
                      targeted={clause.id === targetClauseId || clause.id === focusClauseId}
                      participant={participant}
                      impact={clauseImpact(graphLive, clause.id)}
                      headingById={headingById}
                      onEdit={() => setEditingClause(clause)}
                    />
                    </React.Fragment>
                  );
                })}
                {shownPages < docPageCount && (
                  // Infinite-scroll sentinel: entering the observer's margin
                  // mounts the next chunk. The shimmer doubles as a "more
                  // below" cue on the rare frame it's actually visible.
                  <div ref={loadMoreRef} aria-hidden="true" style={{ padding: 'var(--sp-3) 0' }}>
                    <div className="cs-skeleton" style={{ height: 48 }} />
                  </div>
                )}
              </div>
              )}
            </div>
            {diffing && showExplain && explainCards.length > 0 && (
              <ExplainRail
                cards={explainCards}
                activeId={activeClauseId}
                onJump={jumpToClause}
                baseLabel={`v${compareTo.number} — ${compareTo.label || compareTo.authorName}`}
                currentLabel={`v${current.number}`}
              />
            )}
          </div>

        </>
      )}

      {showVerify && (
        <VerifyModal orgId={orgId} dealId={dealId} docId={docId} onClose={() => setShowVerify(false)} />
      )}

      {showConvert && (
        <ConvertToDeckModal scope={scope} doc={doc} onClose={() => setShowConvert(false)} />
      )}

      {showNewDraft && (
        <NewDraftModal scope={scope} deal={deal} doc={doc} onClose={() => setShowNewDraft(false)} />
      )}

      {replaceFact && official && (
        <ReplaceFactModal
          scope={scope}
          doc={doc}
          official={official}
          dealPoints={dealPoints}
          participant={participant}
          fact={replaceFact.fact}
          initialNext={replaceFact.initialNext}
          reviewKey={replaceFact.reviewKey}
          onClose={() => setReplaceFact(null)}
        />
      )}

      {showChanges && current && (
        <ChangesModal
          scope={scope}
          doc={doc}
          version={current}
          official={official}
          dealPoints={dealPoints}
          participant={participant}
          isDeck={isDeck}
          onClose={() => setShowChanges(false)}
        />
      )}

      {editingClause && current && (
        <ClauseEditor
          scope={scope}
          doc={doc}
          version={current}
          clause={editingClause}
          dealPoints={dealPoints}
          participant={participant}
          impact={clauseImpact(graphLive, editingClause.id)}
          headingById={headingById}
          onClose={() => setEditingClause(null)}
        />
      )}
    </div>
  );
}
